import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppSidebarV2 from "../components/layout/AppSidebarV2";
import Breadcrumbs from "../components/Breadcrumbs";
import BreadcrumbsSchema from "../components/BreadcrumbsSchema";
import { literatureApi } from "../api/literatureApi";
import { useAuth } from "../auth/authStore";
import { useToast } from "../ui/toast/ToastProvider";
import { useNotifications } from "../notifications/notificationsStore";
import { useWs } from "../ws/wsStore";
import { getSiteOrigin, joinUrl } from "../utils/seo";
import { sanitizeLinkHref } from "../utils/safeUrl";
import ReactionParticlesLayer from "../ui/reactions/ReactionParticlesLayer";
import { useReactionParticles } from "../ui/reactions/useReactionParticles";
import {
  DEFAULT_REACTION_KIND,
  REACTION_OPTIONS,
  getReactionPalette,
} from "../ui/reactions/reactionKinds";
import {
  buildNextReactionState as buildUnifiedNextReactionState,
  getReactionBurstPayload,
  normalizeReactionKindForApi,
} from "../ui/reactions/reactionModel";
import {
  isExternalHttpUrl,
  resolvePsychologistProfileRoute,
} from "../utils/psychologistProfile";
import ReportContentButton from "../components/reports/ReportContentButton";

const RECOMMENDATIONS_PAGE_SIZE = 6;
const SIMILAR_PAGE_SIZE = 8;
const COMMENT_LEVEL_INDENT = 26;
const TAB_HASH_MAP = {
  about: "#book-about",
  contents: "#book-contents",
  reviews: "#book-reviews",
};

const DEFAULT_CHAPTER_TITLES = [
  "Что такое тревога",
  "Физиология страха",
  "Когнитивные искажения",
  "Техники экспозиции",
  "Работа с руминацией",
  "Принятие и дефузия",
  "Работа с телом",
  "Профилактика рецидивов",
];

const BOOK_REACTION_PRESETS = REACTION_OPTIONS.map((item) => ({
  type: item.kind,
  emoji: item.emoji,
  style: item.kind === "INSIGHT" || item.kind === "SUPPORT" ? "lav" : "mint",
  label: item.label,
}));
const BOOK_REACTION_ALIASES = {
  CLAP: "APPLAUSE",
  HANDSHAKE: "APPLAUSE",
  IDEA: "INSIGHT",
  LIGHTBULB: "INSIGHT",
  BRAIN: "SUPPORT",
  HEART: "LIKE",
  FAVORITE: "STAR",
};
const REACTION_STATS_META_KEYS = new Set([
  "ACTIVETYPE",
  "MYREACTION",
  "VIEWERREACTION",
  "SELECTEDREACTION",
  "REACTIONTYPE",
  "MYREACTIONTYPE",
  "MYREACTIONKIND",
  "TOTAL",
]);
const BOOK_REACTION_EMOJI_BY_TYPE = BOOK_REACTION_PRESETS.reduce((acc, item) => {
  acc[item.type] = item.emoji;
  return acc;
}, {});
const BOOK_REACTION_STYLE_BY_TYPE = BOOK_REACTION_PRESETS.reduce((acc, item) => {
  acc[item.type] = item.style;
  return acc;
}, {});
const BOOK_REACTION_LABEL_BY_TYPE = BOOK_REACTION_PRESETS.reduce((acc, item) => {
  acc[item.type] = item.label;
  return acc;
}, {});
const BOOK_REACTION_EMOJI_TO_TYPE = BOOK_REACTION_PRESETS.reduce((acc, item) => {
  acc[item.emoji] = item.type;
  return acc;
}, {});

function asObject(value) {
  return value && typeof value === "object" ? value : {};
}

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function unwrapItem(payload) {
  const raw = asObject(payload);
  if (raw.item && typeof raw.item === "object") return raw.item;
  return raw;
}

function unwrapItems(payload) {
  const raw = asObject(payload);
  if (Array.isArray(raw.items)) return raw.items;
  if (raw.items && typeof raw.items === "object") return raw.items;
  return raw;
}

function unwrapPage(payload) {
  const raw = unwrapItems(payload);
  if (raw && typeof raw === "object" && Array.isArray(raw.content)) return raw;
  if (Array.isArray(raw)) {
    return {
      content: raw,
      number: 0,
      size: raw.length,
      totalElements: raw.length,
      totalPages: raw.length ? 1 : 0,
      first: true,
      last: true,
      empty: raw.length === 0,
    };
  }
  return {
    content: [],
    number: 0,
    size: 0,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
    empty: true,
  };
}

function safeNumber(value, fallback = 0) {
  const num = Number(value);
  return Number.isFinite(num) ? num : fallback;
}

function parseTruthyBoolean(value) {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return Number.isFinite(value) && value > 0;
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return false;
  return new Set(["1", "TRUE", "YES", "Y", "ON", "PAID", "PURCHASED", "ACTIVE", "ALLOWED", "FREE"]).has(raw);
}

function normalizeReactionType(value) {
  const raw = String(value || "")
    .trim()
    .toUpperCase();
  if (!raw) return "";
  return normalizeReactionKindForApi(BOOK_REACTION_ALIASES[raw] || raw, "");
}

function typeFromReactionEmoji(value) {
  return BOOK_REACTION_EMOJI_TO_TYPE[String(value || "").trim()] || "";
}

function hasReactionStateData(state) {
  const counts = asObject(state?.counts);
  if (Object.keys(counts).length > 0) return true;
  return Boolean(normalizeReactionType(state?.activeType));
}

function parseReactionStateFromRaw(raw = {}, { fallbackLikeCount = 0 } = {}) {
  const source = asObject(raw);
  const counts = {};
  let activeType = normalizeReactionType(
    source.myReaction ??
      source.viewerReaction ??
      source.selectedReaction ??
      source.reactionType ??
      source.myReactionType ??
      source.myReactionKind ??
      ""
  );

  const pushCount = (typeValue, countValue, active = false) => {
    const type =
      normalizeReactionType(typeValue) ||
      typeFromReactionEmoji(typeValue);
    if (!type) return;
    const nextCount = Math.max(0, safeNumber(countValue, 0));
    counts[type] = Math.max(0, safeNumber(counts[type], 0) + nextCount);
    if (!activeType && active) activeType = type;
  };

  const readFromObjectMap = (valueMap) => {
    Object.entries(asObject(valueMap)).forEach(([key, value]) => {
      const normalizedKey = normalizeReactionType(key);
      if (REACTION_STATS_META_KEYS.has(normalizedKey)) return;
      if (value && typeof value === "object") {
        const item = asObject(value);
        pushCount(
          item.type ??
            item.kind ??
            item.code ??
            item.reactionType ??
            item.emoji ??
            key,
          item.count ?? item.total ?? item.value ?? item.reactionsCount ?? item.likesCount ?? 0,
          Boolean(item.mine || item.active || item.selected || item.me)
        );
        return;
      }
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) return;
      pushCount(key, value, false);
    });
  };

  const candidates = [
    source.reactionStats,
    source.reactions,
    source.reactionCounts,
    source.stats?.reactions,
    source.bookReactions,
  ];

  candidates.forEach((candidate) => {
    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => {
        const item = asObject(entry);
        pushCount(
          item.type ??
            item.kind ??
            item.code ??
            item.reactionType ??
            item.emoji,
          item.count ?? item.total ?? item.value ?? item.reactionsCount ?? item.likesCount ?? 0,
          Boolean(item.mine || item.active || item.selected || item.me)
        );
      });
      return;
    }
    if (candidate && typeof candidate === "object") {
      readFromObjectMap(candidate);
    }
  });

  if (!Object.keys(counts).length) {
    const fallbackCount = Math.max(
      0,
      safeNumber(
        source.reactionsCount ??
          source.likesCount ??
          source.recommendationsCount ??
          fallbackLikeCount,
        0
      )
    );
    if (fallbackCount > 0 || source.likedByMe || source.liked || activeType) {
      counts.LIKE = fallbackCount;
    }
  }

  if (!activeType && (source.likedByMe || source.liked || source.reacted || source.active)) {
    activeType = "LIKE";
  }

  return {
    counts,
    activeType,
  };
}

function buildBookReactionButtons(reactionState, fallbackLikeCount = 0) {
  const sourceCounts = asObject(reactionState?.counts);
  const activeType = normalizeReactionType(reactionState?.activeType);

  const buttons = BOOK_REACTION_PRESETS.map((preset) => {
    const type = normalizeReactionType(preset.type);
    const fallback = type === "LIKE" ? Math.max(0, safeNumber(fallbackLikeCount, 0)) : 0;
    return {
      type,
      emoji: preset.emoji,
      style: preset.style,
      label: preset.label,
      count: Math.max(0, safeNumber(sourceCounts[type], fallback)),
      active: activeType === type,
    };
  });

  const knownTypes = new Set(buttons.map((item) => item.type));
  Object.entries(sourceCounts).forEach(([rawType, count], index) => {
    const type = normalizeReactionType(rawType);
    if (!type || knownTypes.has(type)) return;
    buttons.push({
      type,
      emoji: BOOK_REACTION_EMOJI_BY_TYPE[type] || "👍",
      style: BOOK_REACTION_STYLE_BY_TYPE[type] || (index % 2 === 0 ? "mint" : "lav"),
      label: BOOK_REACTION_LABEL_BY_TYPE[type] || type,
      count: Math.max(0, safeNumber(count, 0)),
      active: activeType === type,
    });
  });

  if (activeType && !knownTypes.has(activeType)) {
    buttons.push({
      type: activeType,
      emoji: BOOK_REACTION_EMOJI_BY_TYPE[activeType] || "👍",
      style: BOOK_REACTION_STYLE_BY_TYPE[activeType] || "mint",
      label: BOOK_REACTION_LABEL_BY_TYPE[activeType] || activeType,
      count: Math.max(1, safeNumber(sourceCounts[activeType], 0)),
      active: true,
    });
  }

  return buttons;
}

function buildNextReactionState(currentState, reactionType) {
  return buildUnifiedNextReactionState(currentState, reactionType);
}

function burstPayloadForReactionType(reactionType) {
  return getReactionBurstPayload(reactionType, DEFAULT_REACTION_KIND);
}

function uniqueBy(items, keyFn) {
  const seen = new Set();
  const out = [];
  asArray(items).forEach((item) => {
    const key = keyFn(item);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(item);
  });
  return out;
}

function normalizeBookCard(raw = {}) {
  const item = asObject(raw);
  const authorInfo = asObject(item.authorProfile || item.authorDetails || item.author || item.psychologist || item.owner || item.createdBy);
  return {
    id: item.id ?? null,
    slug: String(item.slug || ""),
    title: String(item.title || "Без названия"),
    author: String(
      (typeof item.author === "string" ? item.author : authorInfo.name) || "Не указан"
    ),
    authorUserId: authorInfo.userId ?? authorInfo.id ?? authorInfo.createdById ?? authorInfo.ownerId ?? item.authorUserId ?? item.psychologistUserId ?? item.ownerUserId ?? item.createdById ?? null,
    authorProfileUrl: String(authorInfo.profileUrl || authorInfo.url || item.authorProfileUrl || ""),
    coverUrl: String(item.coverUrl || ""),
    shortDescription: String(item.shortDescription || ""),
    primaryTopic: String(item.primaryTopic || ""),
    publishYear: item.publishYear ?? null,
    price: parsePriceNumber(item.price),
    discountPercent: Math.max(0, Math.min(99, safeNumber(item.discountPercent ?? item.discount ?? item.salePercent, 0))),
    priceCurrency: String(item.priceCurrency || item.currency || "RUB").trim().toUpperCase() || "RUB",
    saved: Boolean(item.saved),
  };
}

function normalizeRecommendation(raw = {}) {
  const row = asObject(raw);
  return {
    psychologistUserId: row.psychologistUserId ?? null,
    psychologistName: String(row.psychologistName || "Специалист"),
    psychologistAvatarUrl: String(row.psychologistAvatarUrl || ""),
    psychologistHeadline: String(row.psychologistHeadline || ""),
    subtitle: String(row.subtitle || ""),
    profileUrl: String(row.profileUrl || ""),
    comment: String(row.comment || ""),
  };
}

function normalizeUserSearchItem(raw = {}) {
  const row = asObject(raw);
  const userId = row.userId ?? row.id ?? row.targetUserId ?? null;
  return {
    userId: userId == null ? null : Number(userId),
    login: String(row.login || row.username || row.userName || ""),
    fullName: String(row.fullName || row.name || row.displayName || ""),
    role: String(row.role || ""),
    avatarUrl: String(row.avatarUrl || row.photoUrl || ""),
  };
}

function normalizeBookDetail(raw = {}) {
  const item = asObject(raw);
  const externalStore = asObject(item.externalStore);
  const authorInfo = asObject(item.authorProfile || item.authorDetails || item.author || item.psychologist || item.owner || item.createdBy);
  const accessNode = asObject(item.access || item.myAccess || item.entitlement || item.permissions);
  const accessStatus = String(
    item.accessStatus ??
      item.bookAccessStatus ??
      accessNode.status ??
      accessNode.code ??
      ""
  )
    .trim()
    .toUpperCase();
  const isPurchasedByMe = parseTruthyBoolean(
    item.isPurchasedByMe ??
      item.purchasedByMe ??
      item.purchased ??
      item.isOwnedByMe ??
      item.ownedByMe ??
      item.owned ??
      item.paidByMe ??
      item.myPurchase ??
      accessNode.purchased ??
      accessNode.isPurchased ??
      accessNode.owned ??
      (accessStatus === "PAID" || accessStatus === "PURCHASED")
  );
  const hasReadingAccess = parseTruthyBoolean(
    item.canRead ??
      item.hasReadAccess ??
      item.readAccess ??
      item.isAccessible ??
      item.unlocked ??
      item.isUnlocked ??
      item.availableForMe ??
      accessNode.canRead ??
      accessNode.hasAccess ??
      accessNode.allowed ??
      accessNode.read ??
      isPurchasedByMe ??
      (accessStatus === "FREE" || accessStatus === "ALLOWED" || accessStatus === "ACTIVE")
  );
  return {
    id: item.id ?? null,
    slug: String(item.slug || ""),
    title: String(item.title || "Книга"),
    author: String(
      (typeof item.author === "string" ? item.author : authorInfo.name) || "Не указан"
    ),
    authorUserId: authorInfo.userId ?? authorInfo.id ?? authorInfo.createdById ?? authorInfo.ownerId ?? item.authorUserId ?? item.psychologistUserId ?? item.ownerUserId ?? item.createdById ?? null,
    authorProfileUrl: String(authorInfo.profileUrl || authorInfo.url || item.authorProfileUrl || ""),
    publishYear: item.publishYear ?? null,
    pagesCount: item.pagesCount ?? null,
    shortDescription: String(item.shortDescription || ""),
    description: String(item.description || ""),
    coverUrl: String(item.coverUrl || ""),
    buyUrl: String(item.buyUrl || ""),
    difficultyLabel: String(item.difficultyLabel || item.difficultyLevel || ""),
    categoryLabel: String(item.categoryLabel || ""),
    topics: asArray(item.topics).map((v) => String(v || "").trim()).filter(Boolean),
    tags: asArray(item.tags).map((v) => String(v || "").trim()).filter(Boolean),
    whoFor: asArray(item.whoFor).map((v) => String(v || "").trim()).filter(Boolean),
    recommendationsCount: safeNumber(item.recommendationsCount, 0),
    readersCount: Math.max(
      0,
      safeNumber(
        item.readersCount ??
          item.readers ??
          item.readsCount ??
          item.uniqueReadersCount ??
          item.totalReaders ??
          item.audienceCount ??
          item.recommendationsCount,
        0
      )
    ),
    averageRating: safeNumber(item.averageRating ?? item.rating ?? item.ratingValue, 0),
    ratingsCount: Math.max(
      0,
      safeNumber(item.ratingsCount ?? item.ratingCount ?? item.reviewsCount ?? item.votesCount, 0)
    ),
    price: parsePriceNumber(item.price),
    discountPercent: Math.max(0, Math.min(99, safeNumber(item.discountPercent ?? item.discount ?? item.salePercent, 0))),
    priceCurrency: String(item.priceCurrency || item.currency || "RUB").trim().toUpperCase() || "RUB",
    textVersionUrl: String(
      item.textVersionUrl ??
        item.fileUrl ??
        item.documentUrl ??
        item.textUrl ??
        item.contentUrl ??
        item.bookFileUrl ??
        ""
    ),
    textVersionFormat: String(
      item.textVersionFormat ??
        item.fileFormat ??
        item.textFormat ??
        item.contentFormat ??
        item.bookFileFormat ??
        ""
    )
      .trim()
      .toUpperCase(),
    language: String(item.language || item.lang || "RU").trim().toUpperCase() || "RU",
    readProgressPercent: normalizeReadProgressFromBook(
      item.readProgressPercent ??
        item.readProgress ??
        item.progressPercent ??
        item.progress
    ),
    chapters: asArray(item.chapters),
    shareUrl: String(item.shareUrl || ""),
    shareTitle: String(item.shareTitle || ""),
    shareDescription: String(item.shareDescription || ""),
    recommendations: asArray(item.recommendations).map(normalizeRecommendation),
    similar: asArray(item.similar).map(normalizeBookCard),
    saved: Boolean(item.saved),
    isPurchasedByMe,
    hasReadingAccess,
    externalStore: {
      provider: String(externalStore.provider || ""),
      displayText: String(externalStore.displayText || ""),
      url: String(externalStore.url || ""),
    },
  };
}

function normalizeComment(raw = {}) {
  const item = asObject(raw);
  const author = asObject(item.author || item.user || item.owner);
  const ratingNode = asObject(item.rating);
  const starsNode = asObject(item.stars);
  const scoreNode = asObject(item.score);
  const markNode = asObject(item.mark);
  const gradeNode = asObject(item.grade);
  const voteNode = asObject(item.vote || item.feedback || item.review || item.meta);
  const payloadNode = asObject(item.payload || item.data || item.attributes || item.details);
  return {
    id: item.id ?? null,
    parentCommentId: item.parentCommentId ?? null,
    depth: Math.max(0, safeNumber(item.depth, 0)),
    content: String(item.content || ""),
    authorUserId: item.authorUserId ?? author.userId ?? author.id ?? item.userId ?? null,
    authorName: String(item.authorName || author.name || "Пользователь"),
    authorAvatarUrl: String(item.authorAvatarUrl || author.avatarUrl || ""),
    rating: normalizeStarRating(
      item.rating ??
        item.stars ??
        item.score ??
        item.mark ??
        item.grade ??
        item.bookRating ??
        item.reviewRating ??
        item.userRating ??
        item.starsRating ??
        item.ratingStars ??
        item.reviewStars ??
        item.valueRating ??
        item.value ??
        item.starsCount ??
        item.starsValue ??
        item.ratingValue ??
        payloadNode.rating ??
        payloadNode.stars ??
        payloadNode.score ??
        payloadNode.value ??
        ratingNode.value ??
        ratingNode.score ??
        ratingNode.stars ??
        starsNode.value ??
        starsNode.score ??
        scoreNode.value ??
        markNode.value ??
        gradeNode.value ??
        voteNode.rating ??
        voteNode.stars ??
        voteNode.score ??
        voteNode.value
    ),
    reactionsCount: safeNumber(
      item.reactionsCount ??
        item.reactionsTotal ??
        item.reactionTotal ??
        item.likesCount ??
        item.likedCount ??
        item.likes ??
        item.hearts ??
        payloadNode.reactionsCount ??
        payloadNode.reactionsTotal ??
        payloadNode.likesCount ??
        0,
      0
    ),
    createdWhen:
      item.createdWhen ??
      item.createdAt ??
      item.created ??
      payloadNode.createdWhen ??
      payloadNode.createdAt ??
      null,
    updatedWhen:
      item.updatedWhen ??
      item.updatedAt ??
      item.modifiedWhen ??
      item.modifiedAt ??
      item.lastModifiedAt ??
      payloadNode.updatedWhen ??
      payloadNode.updatedAt ??
      payloadNode.modifiedAt ??
      null,
    likedByMe: Boolean(item.likedByMe),
  };
}

function formatDateTime(value) {
  if (!value) return "без даты";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "без даты";
  return new Intl.DateTimeFormat("ru-RU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function isEditedTimestamp(createdWhen, updatedWhen) {
  if (!createdWhen || !updatedWhen) return false;
  const created = new Date(createdWhen).getTime();
  const updated = new Date(updatedWhen).getTime();
  if (!Number.isFinite(created) || !Number.isFinite(updated)) return false;
  return updated > created + 1000;
}

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function parsePriceNumber(raw) {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return Math.max(0, Number(raw.toFixed(2)));
  const source = String(raw || "")
    .trim()
    .replace(/\s+/g, "")
    .replace(",", ".")
    .replace(/[^\d.]/g, "");
  if (!source) return null;
  const value = Number(source);
  if (!Number.isFinite(value)) return null;
  return Math.max(0, Number(value.toFixed(2)));
}

function formatMoney(value, currency = "RUB") {
  const amount = Math.max(0, safeNumber(value, 0));
  const code = String(currency || "RUB").trim().toUpperCase() || "RUB";
  if (code === "RUB") return `${new Intl.NumberFormat("ru-RU").format(amount)} ₽`;
  try {
    return new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: code,
      maximumFractionDigits: amount % 1 ? 2 : 0,
    }).format(amount);
  } catch {
    return `${new Intl.NumberFormat("ru-RU").format(amount)} ${code}`;
  }
}

function normalizeStarRating(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, Math.min(5, Math.round(numeric)));
}

function normalizeReadProgressFromBook(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return null;
  return clamp(num, 0, 100);
}

function parseFileFormatFromBook(book = {}) {
  const explicit = String(book.textVersionFormat || "")
    .trim()
    .toUpperCase();
  if (explicit) return explicit;
  const url = String(book.textVersionUrl || "").trim();
  if (!url) return "";
  const clean = url.split("#")[0].split("?")[0];
  const tail = clean.split("/").filter(Boolean).pop() || "";
  const dot = tail.lastIndexOf(".");
  if (dot < 0) return "";
  return tail.slice(dot + 1).trim().toUpperCase();
}

function parseReadableParagraphs(rawText = "") {
  const text = String(rawText || "").replace(/\r/g, "").trim();
  if (!text) return [];
  return text
    .split(/\n{2,}/)
    .map((part) => part.trim())
    .filter(Boolean);
}

function parseReadableParagraphsFromJson(rawText = "") {
  const text = String(rawText || "").trim();
  if (!text) return [];
  try {
    const parsed = JSON.parse(text);
    if (typeof parsed === "string") return parseReadableParagraphs(parsed);
    const candidateKeys = [
      "content",
      "text",
      "body",
      "description",
      "shortDescription",
      "summary",
    ];
    const values = [];
    candidateKeys.forEach((key) => {
      const value = parsed?.[key];
      if (typeof value === "string" && value.trim()) values.push(value.trim());
    });
    if (Array.isArray(parsed?.paragraphs)) {
      parsed.paragraphs.forEach((value) => {
        if (typeof value === "string" && value.trim()) values.push(value.trim());
      });
    }
    if (!values.length) return [];
    return parseReadableParagraphs(values.join("\n\n"));
  } catch {
    return [];
  }
}

function stripHtmlToPlainText(html = "") {
  const source = String(html || "").trim();
  if (!source) return "";
  if (typeof window === "undefined" || !window.document) {
    return source.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  }
  const container = window.document.createElement("div");
  container.innerHTML = source;
  return String(container.textContent || container.innerText || "")
    .replace(/\u00A0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function estimateReadHours(pagesCount) {
  const pages = Math.max(0, safeNumber(pagesCount, 0));
  if (!pages) return 0;
  return Math.max(1, Math.round(pages / 60));
}

function splitDescriptionToParagraphs(value) {
  const text = String(value || "").trim();
  if (!text) return [];
  const rows = text
    .split(/\n{2,}/)
    .map((row) => row.trim())
    .filter(Boolean);
  if (rows.length > 1) return rows;
  return (text.match(/[^.!?]+[.!?]?/g) || [])
    .map((row) => row.trim())
    .filter(Boolean);
}

function buildChapters(book, progress) {
  const providedChapters = asArray(book?.chapters)
    .map((chapter, index) => {
      const row = asObject(chapter);
      const rawTitle = row.title ?? row.name ?? row.label ?? row.chapterTitle;
      const rawPages = row.pages ?? row.pageRange ?? row.pagesLabel;
      return {
        num: index + 1,
        title: String(rawTitle || "").trim(),
        pagesLabel: String(rawPages || "").trim() || "—",
      };
    })
    .filter((row) => row.title);
  if (providedChapters.length) {
    const chaptersCount = providedChapters.length;
    const normalizedProgress = clamp(safeNumber(progress, 0), 0, 100);
    const currentIndex = Math.max(
      0,
      Math.min(chaptersCount - 1, Math.floor((normalizedProgress / 100) * chaptersCount))
    );
    return providedChapters.map((chapter, index) => ({
      ...chapter,
      status: index < currentIndex ? "done" : index === currentIndex ? "current" : "unread",
    }));
  }

  const dynamicTitles = [
    ...asArray(book?.topics),
    ...asArray(book?.tags),
    ...asArray(book?.whoFor),
  ]
    .map((item) => String(item || "").trim())
    .filter(Boolean);
  const chapterTitles = (dynamicTitles.length ? dynamicTitles : DEFAULT_CHAPTER_TITLES).slice(0, 8);
  const chaptersCount = chapterTitles.length || 1;
  const pagesTotal = Math.max(0, safeNumber(book?.pagesCount, 0));
  const normalizedProgress = clamp(safeNumber(progress, 0), 0, 100);
  const currentIndex = Math.max(
    0,
    Math.min(chaptersCount - 1, Math.floor((normalizedProgress / 100) * chaptersCount))
  );

  return chapterTitles.map((title, index) => {
    const pagesPerChapter = pagesTotal > 0 ? Math.max(1, Math.floor(pagesTotal / chaptersCount)) : 0;
    const start = pagesPerChapter ? index * pagesPerChapter + 1 : 0;
    const end =
      pagesPerChapter && index === chaptersCount - 1 ? pagesTotal : pagesPerChapter ? (index + 1) * pagesPerChapter : 0;

    let status = "unread";
    if (index < currentIndex) status = "done";
    else if (index === currentIndex) status = "current";

    return {
      num: index + 1,
      title,
      pagesLabel: pagesPerChapter ? `${start}-${end}` : "—",
      status,
    };
  });
}

function tabFromHash(hash) {
  const normalized = String(hash || "").trim().toLowerCase();
  if (normalized === "#book-reviews") return "reviews";
  if (normalized === "#book-contents") return "contents";
  return "about";
}

function displayRole(role) {
  const value = String(role || "").toUpperCase();
  if (value === "ADMIN") return "Администратор";
  if (value === "PSYCHOLOGIST") return "Психолог";
  if (value === "MODERATOR") return "Модератор";
  if (value === "CLIENT") return "Клиент";
  return "Гость";
}

function formatNumber(value) {
  return new Intl.NumberFormat("ru-RU").format(Math.max(0, safeNumber(value, 0)));
}

function initials(value) {
  const chunks = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((chunk) => chunk[0]);
  const out = chunks.join("").toUpperCase();
  return out || "BS";
}

function makeBookRoute(book) {
  const ident = book?.slug || book?.id;
  return ident == null ? "" : `/literature/books/${encodeURIComponent(String(ident))}`;
}

function parsePositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parseLiteratureBookRefFromUrl(rawUrl = "") {
  const value = String(rawUrl || "").trim();
  if (!value) return "";
  const match = value.match(/\/literature\/books\/([^?#/]+)/i);
  return String(match?.[1] || "").trim();
}

function extractLiteratureRealtimeMeta(payload) {
  if (!payload || typeof payload !== "object") return null;

  const dto =
    payload.notification && typeof payload.notification === "object"
      ? payload.notification
      : payload;
  const dtoPayload = dto.payload && typeof dto.payload === "object" ? dto.payload : {};
  const rawType = String(
    dtoPayload.action ||
      payload.action ||
      dto.action ||
      payload.eventType ||
      dto.eventType ||
      payload.type ||
      dto.type ||
      ""
  )
    .trim()
    .toUpperCase();

  const bookRefRaw =
    dtoPayload.bookId ??
    dto.bookId ??
    dtoPayload.literatureBookId ??
    dto.literatureBookId ??
    dtoPayload.book?.id ??
    dto.book?.id ??
    dtoPayload.book?.slug ??
    dto.book?.slug ??
    parseLiteratureBookRefFromUrl(
      dtoPayload.linkUrl || dto.linkUrl || payload.linkUrl || ""
    );
  const commentId = parsePositiveId(
    dtoPayload.commentId ?? dto.commentId ?? dtoPayload.comment?.id ?? dto.comment?.id
  );
  const reactionType = normalizeReactionType(
    dtoPayload.reactionType ??
      dto.reactionType ??
      dtoPayload.kind ??
      dto.kind ??
      dtoPayload.type ??
      dto.type
  );
  const isLiteratureAction =
    rawType.includes("LITERATURE") || rawType.includes("BOOK_COMMENT") || rawType.includes("BOOK_REACTION");

  const normalizedBookRef = String(bookRefRaw || "").trim();
  if (!isLiteratureAction && !normalizedBookRef && !commentId) return null;

  return {
    type: rawType || "LITERATURE_UPDATED",
    bookRef: normalizedBookRef,
    commentId,
    reactionType,
  };
}

function flattenComments(items = [], { sort = "new" } = {}) {
  const nodes = asArray(items).map((comment) => ({
    ...comment,
    children: [],
  }));
  const nodeById = new Map();
  nodes.forEach((node) => {
    if (node.id != null) nodeById.set(String(node.id), node);
  });

  const roots = [];
  nodes.forEach((node) => {
    const parentId = node.parentCommentId;
    if (parentId == null) {
      roots.push(node);
      return;
    }
    const parent = nodeById.get(String(parentId));
    if (!parent) {
      roots.push(node);
      return;
    }
    parent.children.push(node);
  });

  const byCreated = (a, b) => {
    const left = a?.createdWhen ? new Date(a.createdWhen).getTime() : 0;
    const right = b?.createdWhen ? new Date(b.createdWhen).getTime() : 0;
    return left - right;
  };
  const byCreatedDesc = (a, b) => byCreated(b, a);
  const byPopular = (a, b) => {
    const diff = safeNumber(b?.reactionsCount, 0) - safeNumber(a?.reactionsCount, 0);
    if (diff !== 0) return diff;
    return byCreatedDesc(a, b);
  };
  if (sort === "popular") roots.sort(byPopular);
  else roots.sort(byCreatedDesc);
  nodes.forEach((node) => node.children.sort(byCreated));

  const flat = [];
  const walk = (node, level) => {
    flat.push({
      ...node,
      level: Math.max(node.depth || 0, level),
    });
    node.children.forEach((child) => walk(child, level + 1));
  };
  roots.forEach((root) => walk(root, 0));
  return flat;
}

export default function LiteratureBookDetails() {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const { bookIdOrSlug = "" } = useParams();
  const { role, isAuthenticated, me, logout } = useAuth();
  const { unreadNotificationsCount = 0 } = useNotifications();
  const { connected: wsConnected, subscribe: wsSubscribe } = useWs();
  const realtimeRefreshTimerRef = React.useRef(null);
  const readProgressPersistTimerRef = React.useRef(null);
  const pdfContainerRef = React.useRef(null);
  const pdfCanvasRef = React.useRef(null);
  const pdfDocumentRef = React.useRef(null);
  const pdfRenderTaskRef = React.useRef(null);

  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [searchInput, setSearchInput] = React.useState("");

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [book, setBook] = React.useState(() => normalizeBookDetail({}));
  const [bookReactionState, setBookReactionState] = React.useState(() => ({ counts: {}, activeType: "" }));

  const [commentsLoading, setCommentsLoading] = React.useState(true);
  const [comments, setComments] = React.useState([]);
  const [commentText, setCommentText] = React.useState("");
  const [commentRating, setCommentRating] = React.useState(0);
  const [replyTo, setReplyTo] = React.useState(null);
  const [replyText, setReplyText] = React.useState("");
  const [editingComment, setEditingComment] = React.useState(null);
  const [editingText, setEditingText] = React.useState("");
  const [editingRating, setEditingRating] = React.useState(0);
  const [editingBusy, setEditingBusy] = React.useState(false);
  const [sendingComment, setSendingComment] = React.useState(false);
  const [reactionBusy, setReactionBusy] = React.useState(() => new Set());
  const [savingBook, setSavingBook] = React.useState(false);
  const [likingBook, setLikingBook] = React.useState(false);

  const [recommendations, setRecommendations] = React.useState([]);
  const [recommendationsLoading, setRecommendationsLoading] = React.useState(false);
  const [recommendationsTotal, setRecommendationsTotal] = React.useState(0);
  const [similarBooks, setSimilarBooks] = React.useState([]);
  const [similarLoading, setSimilarLoading] = React.useState(false);

  const [activeTab, setActiveTab] = React.useState("about");
  const [reviewsSort, setReviewsSort] = React.useState("new");
  const [readModalOpen, setReadModalOpen] = React.useState(false);
  const [buyModalOpen, setBuyModalOpen] = React.useState(false);
  const [recommendModalOpen, setRecommendModalOpen] = React.useState(false);
  const [recommendQuery, setRecommendQuery] = React.useState("");
  const [recommendResults, setRecommendResults] = React.useState([]);
  const [recommendLoading, setRecommendLoading] = React.useState(false);
  const [recommendError, setRecommendError] = React.useState("");
  const [recommendRecipient, setRecommendRecipient] = React.useState(null);
  const [recommendComment, setRecommendComment] = React.useState("");
  const [recommendSending, setRecommendSending] = React.useState(false);
  const [buySubmitting, setBuySubmitting] = React.useState(false);
  const [readTheme, setReadTheme] = React.useState("white");
  const [readFontSize, setReadFontSize] = React.useState(15);
  const pdfZoomScale = React.useMemo(() => {
    if (readFontSize <= 14) return 0.9;
    if (readFontSize >= 17) return 1.2;
    return 1;
  }, [readFontSize]);
  const [readProgress, setReadProgress] = React.useState(0);
  const readProgressValueRef = React.useRef(0);
  const [readContentLoading, setReadContentLoading] = React.useState(false);
  const [readContentError, setReadContentError] = React.useState("");
  const [readParagraphs, setReadParagraphs] = React.useState([]);
  const [readEmbedMode, setReadEmbedMode] = React.useState("");
  const [pdfPagesTotal, setPdfPagesTotal] = React.useState(0);
  const [pdfCurrentPage, setPdfCurrentPage] = React.useState(1);
  const [pdfRenderNonce, setPdfRenderNonce] = React.useState(0);
  const {
    particles: reactionParticles,
    emitBurstFromEvent: emitReactionBurstFromEvent,
  } = useReactionParticles({
    particleCount: 12,
    floaterCount: 5,
    dotCount: 12,
    rippleCount: 2,
    minDistance: 26,
    maxDistance: 76,
    minSize: 14,
    maxSize: 22,
    minDuration: 620,
    maxDuration: 980,
    minDotSize: 4,
    maxDotSize: 8,
    minDotDuration: 340,
    maxDotDuration: 620,
    rippleLifeMs: 560,
  });

  React.useEffect(() => {
    readProgressValueRef.current = clamp(readProgress, 0, 100);
  }, [readProgress]);

  const origin = React.useMemo(() => getSiteOrigin(), []);
  const canonical = React.useMemo(
    () => joinUrl(origin, `/literature/books/${encodeURIComponent(String(bookIdOrSlug || ""))}`),
    [origin, bookIdOrSlug]
  );
  const breadcrumbs = React.useMemo(
    () => [
      { label: "Главная", to: "/" },
      { label: "Литература", to: "/literature" },
      { label: book.title || "Книга" },
    ],
    [book.title]
  );
  const roleLabel = React.useMemo(() => displayRole(role), [role]);
  const myUserId = React.useMemo(() => parsePositiveId(me?.id ?? me?.userId), [me?.id, me?.userId]);
  const canManageBooks = role === "ADMIN" || role === "MODERATOR" || role === "PSYCHOLOGIST";

  const readStorageKey = React.useMemo(() => {
    const identity = book.id ?? book.slug ?? bookIdOrSlug;
    const userIdent = me?.id ?? me?.userId ?? me?.login ?? "anon";
    return `lit:read-progress:${String(userIdent)}:${String(identity || "book")}`;
  }, [book.id, book.slug, bookIdOrSlug, me?.id, me?.login, me?.userId]);
  const bookFileFormat = React.useMemo(() => parseFileFormatFromBook(book), [book]);
  const bookFileUrl = React.useMemo(
    () =>
      sanitizeLinkHref(book.textVersionUrl || "", {
        allowRelative: true,
        allowMailto: false,
        allowTel: false,
        allowHash: false,
      }),
    [book.textVersionUrl]
  );
  const shareLink = React.useMemo(() => {
    const raw = String(book.shareUrl || "").trim();
    const safe = sanitizeLinkHref(raw, {
      allowRelative: true,
      allowMailto: false,
      allowTel: false,
      allowHash: false,
    });
    if (safe) {
      if (isExternalHttpUrl(safe)) return safe;
      if (typeof window !== "undefined") {
        try {
          return new URL(safe, window.location.origin).toString();
        } catch {}
      }
    }
    if (canonical && isExternalHttpUrl(canonical)) return canonical;
    if (typeof window !== "undefined") return window.location.href;
    return "";
  }, [book.shareUrl, canonical]);

  const applyBookPayload = React.useCallback((rawPayload) => {
    const payload = unwrapItem(rawPayload);
    const normalized = normalizeBookDetail(payload);
    setBook(normalized);
    setRecommendations(normalized.recommendations);
    setRecommendationsTotal(
      Math.max(
        0,
        safeNumber(normalized.readersCount, 0),
        safeNumber(normalized.recommendationsCount, normalized.recommendations.length)
      )
    );
    setSimilarBooks(normalized.similar);
    setBookReactionState(
      parseReactionStateFromRaw(payload, {
        fallbackLikeCount: Math.max(0, safeNumber(normalized.recommendationsCount, 0)),
      })
    );
    if (normalized.readProgressPercent != null) {
      setReadProgress(normalized.readProgressPercent);
    }
  }, []);

  const updateReadProgress = React.useCallback(
    (nextValue, { persistRemote = true } = {}) => {
      const normalized = clamp(safeNumber(nextValue, 0), 0, 100);
      setReadProgress(normalized);
      if (typeof window !== "undefined") {
        try {
          window.localStorage.setItem(readStorageKey, String(normalized));
        } catch {}
      }
      if (!persistRemote || !isAuthenticated || !book.id) return;
      if (readProgressPersistTimerRef.current) {
        window.clearTimeout(readProgressPersistTimerRef.current);
      }
      readProgressPersistTimerRef.current = window.setTimeout(() => {
        readProgressPersistTimerRef.current = null;
        literatureApi
          .updateMyBookProgress(book.id, { progressPercent: normalized })
          .catch(() => {});
      }, 500);
    },
    [book.id, isAuthenticated, readStorageKey]
  );

  React.useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(readStorageKey);
      if (raw == null) {
        setReadProgress(0);
        return;
      }
      const parsed = Number(raw);
      setReadProgress(clamp(Number.isFinite(parsed) ? parsed : 0, 0, 100));
    } catch {
      setReadProgress(0);
    }
  }, [readStorageKey]);

  React.useEffect(() => {
    return () => {
      if (readProgressPersistTimerRef.current) {
        window.clearTimeout(readProgressPersistTimerRef.current);
        readProgressPersistTimerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setReplyTo(null);
    setReplyText("");
    setRecommendations([]);
    setRecommendationsTotal(0);
    setSimilarBooks([]);
    setBookReactionState({ counts: {}, activeType: "" });

    if (!bookIdOrSlug) {
      setLoading(false);
      setError("Книга не найдена");
      return undefined;
    }

    literatureApi
      .getBook(bookIdOrSlug)
      .then((response) => {
        if (cancelled) return;
        applyBookPayload(response);
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError(fetchError?.message || "Не удалось загрузить книгу");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [applyBookPayload, bookIdOrSlug]);

  React.useEffect(() => {
    setActiveTab(tabFromHash(location.hash));
  }, [location.hash]);

  React.useEffect(() => {
    if (!isAuthenticated || !book.id) return undefined;
    let cancelled = false;
    literatureApi
      .getMyBookProgress(book.id)
      .then((response) => {
        if (cancelled) return;
        const payload = unwrapItem(response);
        const remote = normalizeReadProgressFromBook(
          payload?.progressPercent ??
            payload?.readProgressPercent ??
            payload?.progress ??
            payload?.readProgress
        );
        if (remote == null) return;
        updateReadProgress(remote, { persistRemote: false });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [book.id, isAuthenticated, updateReadProgress]);

  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const id = String(location.hash || "").replace(/^#/, "");
    if (!id) return;
    const timer = window.setTimeout(() => {
      const target = document.getElementById(id);
      if (!target) return;
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeTab, location.hash]);

  const loadComments = React.useCallback(async () => {
    if (!bookIdOrSlug) return;
    setCommentsLoading(true);
    try {
      const response = await literatureApi.getBookComments(bookIdOrSlug);
      const normalized = asArray(unwrapItems(response)).map(normalizeComment);
      setComments(normalized);
    } catch {
      setComments([]);
    } finally {
      setCommentsLoading(false);
    }
  }, [bookIdOrSlug]);

  const reloadBookRealtime = React.useCallback(async () => {
    if (!bookIdOrSlug) return;
    try {
      const response = await literatureApi.getBook(bookIdOrSlug);
      applyBookPayload(response);
    } catch {
      // ignore realtime refresh errors
    }
  }, [applyBookPayload, bookIdOrSlug]);

  const scheduleRealtimeRefresh = React.useCallback(() => {
    if (realtimeRefreshTimerRef.current) {
      window.clearTimeout(realtimeRefreshTimerRef.current);
    }
    realtimeRefreshTimerRef.current = window.setTimeout(() => {
      realtimeRefreshTimerRef.current = null;
      void reloadBookRealtime();
      void loadComments();
    }, 180);
  }, [loadComments, reloadBookRealtime]);

  React.useEffect(() => {
    loadComments();
  }, [loadComments]);

  React.useEffect(() => {
    return () => {
      if (realtimeRefreshTimerRef.current) {
        window.clearTimeout(realtimeRefreshTimerRef.current);
        realtimeRefreshTimerRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || !wsConnected || !bookIdOrSlug) return undefined;

    const currentRefs = new Set(
      [bookIdOrSlug, book.id, book.slug]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    );

    const matchesCurrentBook = (bookRef) => {
      const normalized = String(bookRef || "").trim();
      if (!normalized) return false;
      if (currentRefs.has(normalized)) return true;
      let decoded = normalized;
      try {
        decoded = decodeURIComponent(normalized);
      } catch {}
      return currentRefs.has(decoded);
    };

    const onRealtimePayload = (payload) => {
      const meta = extractLiteratureRealtimeMeta(payload);
      if (!meta) return;
      if (!meta.bookRef) return;
      if (meta.bookRef && !matchesCurrentBook(meta.bookRef)) return;
      scheduleRealtimeRefresh();
    };

    const unsubscribers = [wsSubscribe("/user/queue/notifications", onRealtimePayload)];
    const topicKey =
      String(book.id || "").trim() ||
      String(book.slug || "").trim() ||
      String(bookIdOrSlug || "").trim();
    if (topicKey) {
      unsubscribers.push(
        wsSubscribe(`/topic/literature/books/${encodeURIComponent(topicKey)}`, onRealtimePayload)
      );
    }

    return () => {
      unsubscribers.forEach((unsub) => {
        try {
          unsub?.();
        } catch {}
      });
    };
  }, [
    book.id,
    book.slug,
    bookIdOrSlug,
    isAuthenticated,
    scheduleRealtimeRefresh,
    wsConnected,
    wsSubscribe,
  ]);

  React.useEffect(() => {
    if (!bookIdOrSlug) return;
    let cancelled = false;
    setRecommendationsLoading(true);
    literatureApi
      .getBookRecommendations(bookIdOrSlug, { page: 0, size: RECOMMENDATIONS_PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        const pageData = unwrapPage(response);
        const content = asArray(pageData.content).map(normalizeRecommendation);
        setRecommendations(content);
        setRecommendationsTotal((prev) =>
          Math.max(prev, 0, safeNumber(pageData.totalElements, content.length))
        );
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setRecommendationsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookIdOrSlug]);

  React.useEffect(() => {
    if (!bookIdOrSlug) return;
    let cancelled = false;
    setSimilarLoading(true);
    literatureApi
      .getBookSimilar(bookIdOrSlug, { page: 0, size: SIMILAR_PAGE_SIZE })
      .then((response) => {
        if (cancelled) return;
        const pageData = unwrapPage(response);
        const content = asArray(pageData.content).map(normalizeBookCard);
        setSimilarBooks(content);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setSimilarLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookIdOrSlug]);

  React.useEffect(() => {
    if (!recommendModalOpen) return;
    const query = String(recommendQuery || "").trim();
    if (query.length < 2) {
      setRecommendLoading(false);
      setRecommendError("");
      setRecommendResults([]);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
        setRecommendLoading(true);
        setRecommendError("");
        try {
          const response = await literatureApi.searchUsers({
            q: query,
            // backend supports role=ANY/ALL/* or empty (no filter)
            role: "ANY",
            limit: 20,
          });
          if (cancelled) return;
          const myId = Number(me?.id ?? me?.userId);
          const rows = asArray(unwrapItems(response))
          .map(normalizeUserSearchItem)
          .filter((item) => Number.isFinite(item.userId) && item.userId > 0)
          .filter((item) => !Number.isFinite(myId) || item.userId !== myId);
        setRecommendResults(rows);
      } catch (errorValue) {
        if (cancelled) return;
        setRecommendResults([]);
        setRecommendError(errorValue?.message || "Не удалось найти пользователей");
      } finally {
        if (!cancelled) setRecommendLoading(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [me?.id, me?.userId, recommendModalOpen, recommendQuery]);

  React.useEffect(() => {
    if (recommendModalOpen) return;
    setRecommendQuery("");
    setRecommendResults([]);
    setRecommendError("");
    setRecommendLoading(false);
    setRecommendRecipient(null);
    setRecommendComment("");
    setRecommendSending(false);
  }, [recommendModalOpen]);

  React.useEffect(() => {
    if (!readModalOpen) return undefined;
    const format = String(bookFileFormat || "").toUpperCase();
    const url = String(bookFileUrl || "").trim();

    setReadContentError("");
    setReadContentLoading(false);
    setReadEmbedMode("");
    setReadParagraphs([]);
    setPdfPagesTotal(0);
    setPdfCurrentPage(1);
    setPdfRenderNonce(0);
    if (pdfRenderTaskRef.current?.cancel) {
      try {
        pdfRenderTaskRef.current.cancel();
      } catch {}
    }
    pdfRenderTaskRef.current = null;
    const previousPdfDoc = pdfDocumentRef.current;
    pdfDocumentRef.current = null;
    if (previousPdfDoc?.destroy) {
      Promise.resolve(previousPdfDoc.destroy()).catch(() => {});
    }

    if (!url) {
      setReadContentError("Файл книги не загружен. Добавьте файл в редакторе книги.");
      return undefined;
    }

    if (format === "PDF") {
      setReadEmbedMode("pdf");
      setReadContentLoading(true);

      let cancelled = false;
      let loadingTask = null;
      void (async () => {
        try {
          const module = await import("pdfjs-dist/build/pdf");
          const pdfjsLib = module?.default || module;
          if (!pdfjsLib?.getDocument) throw new Error("PDFJS_UNAVAILABLE");

          const publicBase = String(process.env.PUBLIC_URL || "");
          if (pdfjsLib.GlobalWorkerOptions) {
            pdfjsLib.GlobalWorkerOptions.workerSrc = `${publicBase}/pdf.worker.min.js`;
          }

          loadingTask = pdfjsLib.getDocument({
            url,
            withCredentials: true,
          });
          const pdfDoc = await loadingTask.promise;
          if (cancelled) {
            await Promise.resolve(pdfDoc?.destroy?.()).catch(() => {});
            return;
          }
          pdfDocumentRef.current = pdfDoc;
          const totalPages = Math.max(1, safeNumber(pdfDoc.numPages, 1));
          setPdfPagesTotal(totalPages);
          const initialPage = clamp(
            Math.round((readProgressValueRef.current / 100) * totalPages) || 1,
            1,
            totalPages
          );
          setPdfCurrentPage(initialPage);
        } catch {
          if (!cancelled) {
            setReadContentError("Не удалось загрузить PDF для чтения.");
          }
        } finally {
          if (!cancelled) {
            setReadContentLoading(false);
          }
        }
      })();

      return () => {
        cancelled = true;
        try {
          loadingTask?.destroy?.();
        } catch {}
        if (pdfRenderTaskRef.current?.cancel) {
          try {
            pdfRenderTaskRef.current.cancel();
          } catch {}
        }
        pdfRenderTaskRef.current = null;
        const currentPdfDoc = pdfDocumentRef.current;
        pdfDocumentRef.current = null;
        if (currentPdfDoc?.destroy) {
          Promise.resolve(currentPdfDoc.destroy()).catch(() => {});
        }
      };
    }

    const canFetchText = ["TXT", "TEXT", "MD", "MARKDOWN", "HTML", "HTM", "JSON"].includes(format);
    if (!canFetchText) {
      setReadEmbedMode("external");
      setReadContentError("Формат файла не поддерживает встроенное чтение. Откройте файл по кнопке выше.");
      return undefined;
    }

    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    setReadContentLoading(true);

    fetch(url, {
      method: "GET",
      credentials: "include",
      signal: controller?.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const text = await response.text();
        let parsed = [];
        if (format === "HTML" || format === "HTM") {
          parsed = parseReadableParagraphs(stripHtmlToPlainText(text));
        } else if (format === "JSON") {
          parsed = parseReadableParagraphsFromJson(text);
        } else {
          parsed = parseReadableParagraphs(text);
        }
        return parsed;
      })
      .then((parsed) => {
        if (parsed.length) {
          setReadParagraphs(parsed);
          return;
        }
        setReadContentError("В загруженном файле не найден текст для отображения.");
      })
      .catch((errorValue) => {
        if (errorValue?.name === "AbortError") return;
        setReadContentError("Не удалось загрузить файл книги для чтения.");
      })
      .finally(() => {
        setReadContentLoading(false);
      });

    return () => {
      controller?.abort?.();
    };
  }, [bookFileFormat, bookFileUrl, readModalOpen]);

  React.useEffect(() => {
    if (!readModalOpen || readEmbedMode !== "pdf") return undefined;
    if (!pdfPagesTotal || !pdfCurrentPage) return undefined;
    const canvas = pdfCanvasRef.current;
    const pdfDoc = pdfDocumentRef.current;
    if (!canvas || !pdfDoc) return undefined;

    let cancelled = false;
    const renderPage = async () => {
      try {
        const page = await pdfDoc.getPage(pdfCurrentPage);
        if (cancelled) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const containerWidth = Math.max(
          320,
          safeNumber(pdfContainerRef.current?.clientWidth, baseViewport.width)
        );
        const targetWidth = Math.max(280, containerWidth - 28);
        const scale = clamp((targetWidth / Math.max(1, baseViewport.width)) * pdfZoomScale, 0.35, 3.2);
        const viewport = page.getViewport({ scale });
        const pixelRatio =
          typeof window !== "undefined" && Number.isFinite(window.devicePixelRatio)
            ? Math.max(1, window.devicePixelRatio)
            : 1;

        const context = canvas.getContext("2d", { alpha: false });
        if (!context) throw new Error("CANVAS_CONTEXT");

        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${Math.floor(viewport.width)}px`;
        canvas.style.height = `${Math.floor(viewport.height)}px`;
        context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
        context.clearRect(0, 0, viewport.width, viewport.height);

        if (pdfRenderTaskRef.current?.cancel) {
          try {
            pdfRenderTaskRef.current.cancel();
          } catch {}
        }
        const renderTask = page.render({ canvasContext: context, viewport });
        pdfRenderTaskRef.current = renderTask;
        await renderTask.promise;
      } catch (errorValue) {
        if (cancelled) return;
        if (errorValue?.name === "RenderingCancelledException") return;
        setReadContentError("Не удалось отрисовать страницу PDF.");
      }
    };

    void renderPage();

    return () => {
      cancelled = true;
      if (pdfRenderTaskRef.current?.cancel) {
        try {
          pdfRenderTaskRef.current.cancel();
        } catch {}
      }
      pdfRenderTaskRef.current = null;
    };
  }, [pdfCurrentPage, pdfPagesTotal, pdfRenderNonce, pdfZoomScale, readEmbedMode, readModalOpen]);

  React.useEffect(() => {
    if (!readModalOpen || readEmbedMode !== "pdf" || !pdfPagesTotal) return;
    const normalized = clamp(Math.round((pdfCurrentPage / pdfPagesTotal) * 100), 0, 100);
    if (normalized === readProgress) return;
    updateReadProgress(normalized);
  }, [
    pdfCurrentPage,
    pdfPagesTotal,
    readEmbedMode,
    readModalOpen,
    readProgress,
    updateReadProgress,
  ]);

  React.useEffect(() => {
    if (!readModalOpen || readEmbedMode !== "pdf") return undefined;
    if (typeof window === "undefined") return undefined;
    const onResize = () => setPdfRenderNonce((prev) => prev + 1);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [readEmbedMode, readModalOpen]);

  React.useEffect(() => {
    const lockScroll = readModalOpen || buyModalOpen || recommendModalOpen;
    if (!lockScroll) return undefined;
    if (typeof document === "undefined") return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [readModalOpen, buyModalOpen, recommendModalOpen]);

  React.useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;
      setReadModalOpen(false);
      setBuyModalOpen(false);
      setRecommendModalOpen(false);
    };
    if (typeof document === "undefined") return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleToggleSave = React.useCallback(async () => {
    if (!book.id || savingBook) return;
    if (!isAuthenticated) {
      toast.info("Сохранение доступно после авторизации");
      navigate("/login");
      return;
    }
    setSavingBook(true);
    try {
      if (book.saved) await literatureApi.unsaveBook(book.id);
      else await literatureApi.saveBook(book.id);
      setBook((prev) => ({ ...prev, saved: !prev.saved }));
      toast.success(book.saved ? "Удалено из сохранённых" : "Книга сохранена");
    } catch (errorValue) {
      toast.error(errorValue?.message || "Не удалось обновить сохранение");
    } finally {
      setSavingBook(false);
    }
  }, [book.id, book.saved, isAuthenticated, navigate, savingBook, toast]);

  const handleToggleBookReaction = React.useCallback(
    async (event, reactionType = "LIKE") => {
      if (likingBook || !book.id) return;
      if (!isAuthenticated) {
        toast.info("Реакции доступны после авторизации");
        navigate("/login");
        return;
      }

      const normalizedType = normalizeReactionType(reactionType) || "LIKE";
      const burst = burstPayloadForReactionType(normalizedType);
      emitReactionBurstFromEvent(event, {
        emoji: burst.emoji,
        kind: burst.kind,
        colors: burst.colors,
      });

      const prevState = {
        counts: { ...asObject(bookReactionState.counts) },
        activeType: normalizeReactionType(bookReactionState.activeType),
      };
      const optimisticState = buildNextReactionState(prevState, normalizedType);
      setBookReactionState(optimisticState);

      setLikingBook(true);
      try {
        const response = await literatureApi.toggleBookReaction(book.id, normalizedType);
        const payload = unwrapItem(response);
        const parsedState = parseReactionStateFromRaw(payload, {
          fallbackLikeCount: safeNumber(optimisticState?.counts?.LIKE, 0),
        });

        if (hasReactionStateData(parsedState)) {
          setBookReactionState(parsedState);
          return;
        }

        const hasSimplePayload =
          typeof payload?.liked === "boolean" ||
          typeof payload?.active === "boolean" ||
          payload?.count !== undefined ||
          payload?.reactionsCount !== undefined;

        if (hasSimplePayload) {
          const serverLiked = Boolean(
            payload?.liked ??
              payload?.active ??
              payload?.reacted ??
              payload?.selected ??
              payload?.mine
          );
          const serverCount = Math.max(
            0,
            safeNumber(
              payload?.count ??
                payload?.reactionsCount ??
                payload?.likesCount,
              optimisticState?.counts?.[normalizedType] ?? 0
            )
          );
          setBookReactionState((current) => {
            const counts = { ...asObject(current?.counts), [normalizedType]: serverCount };
            const currentType = normalizeReactionType(current?.activeType);
            return {
              counts,
              activeType: serverLiked
                ? normalizedType
                : currentType === normalizedType
                ? ""
                : currentType,
            };
          });
        }
      } catch (errorValue) {
        setBookReactionState(prevState);
        toast.error(errorValue?.message || "Не удалось отправить реакцию");
      } finally {
        setLikingBook(false);
      }
    },
    [
      book.id,
      bookReactionState.activeType,
      bookReactionState.counts,
      emitReactionBurstFromEvent,
      isAuthenticated,
      likingBook,
      navigate,
      toast,
    ]
  );

  const handleToggleCommentReaction = React.useCallback(
    async (event, commentId) => {
      if (!commentId) return;
      if (reactionBusy.has(String(commentId))) return;
      if (!isAuthenticated) {
        toast.info("Реакции к комментариям доступны после авторизации");
        navigate("/login");
        return;
      }

      const previousComment = asArray(comments).find(
        (comment) => String(comment.id) === String(commentId)
      );
      if (!previousComment) return;

      emitReactionBurstFromEvent(event, {
        emoji: "❤️",
        kind: DEFAULT_REACTION_KIND,
        colors: getReactionPalette(DEFAULT_REACTION_KIND),
      });

      setComments((prev) =>
        asArray(prev).map((comment) => {
          if (String(comment.id) !== String(commentId)) return comment;
          const nextLiked = !Boolean(comment.likedByMe);
          const currentCount = Math.max(0, safeNumber(comment.reactionsCount, 0));
          return {
            ...comment,
            likedByMe: nextLiked,
            reactionsCount: Math.max(0, currentCount + (nextLiked ? 1 : -1)),
          };
        })
      );

      setReactionBusy((prev) => {
        const next = new Set(prev);
        next.add(String(commentId));
        return next;
      });
      try {
        const response = await literatureApi.toggleCommentReaction(commentId, "LIKE");
        const item = unwrapItem(response);
        setComments((prev) =>
          asArray(prev).map((comment) =>
            String(comment.id) === String(commentId)
              ? {
                  ...comment,
                  likedByMe: Boolean(
                    item?.liked ??
                      item?.active ??
                      item?.reacted ??
                      item?.selected ??
                      item?.mine
                  ),
                  reactionsCount: Math.max(
                    0,
                    safeNumber(item?.count ?? item?.reactionsCount ?? item?.likesCount, 0)
                  ),
                }
              : comment
          )
        );
      } catch (errorValue) {
        setComments((prev) =>
          asArray(prev).map((comment) =>
            String(comment.id) === String(commentId) ? previousComment : comment
          )
        );
        toast.error(errorValue?.message || "Не удалось обновить реакцию");
      } finally {
        setReactionBusy((prev) => {
          const next = new Set(prev);
          next.delete(String(commentId));
          return next;
        });
      }
    },
    [comments, emitReactionBurstFromEvent, isAuthenticated, navigate, reactionBusy, toast]
  );

  const myRatedReview = React.useMemo(() => {
    if (!myUserId) return null;
    return (
      comments.find((comment) => {
        if (comment.parentCommentId != null) return false;
        if (parsePositiveId(comment.authorUserId) !== myUserId) return false;
        return normalizeStarRating(comment.rating) > 0;
      }) || null
    );
  }, [comments, myUserId]);
  const canSetRatingOnNewReview = !myRatedReview;

  const submitCommentPayload = React.useCallback(
    async (content, parentCommentId = null, rating = 0) => {
      if (sendingComment) return false;
      if (!book.id) return false;
      const normalizedContent = String(content || "").trim();
      const normalizedRating = normalizeStarRating(rating);
      if (!normalizedContent) return false;
      if (!isAuthenticated) {
        toast.info("Комментарии доступны после авторизации");
        navigate("/login");
        return false;
      }
      if (parentCommentId == null && canSetRatingOnNewReview && normalizedRating < 1) {
        toast.info("Поставьте оценку книге перед публикацией отзыва");
        return false;
      }

      setSendingComment(true);
      try {
        await literatureApi.createBookComment(book.id, {
          parentCommentId,
          content: normalizedContent,
          rating:
            parentCommentId == null && canSetRatingOnNewReview
              ? normalizedRating
              : null,
        });
        await loadComments();
        toast.success("Комментарий опубликован");
        return true;
      } catch (errorValue) {
        toast.error(errorValue?.message || "Не удалось отправить комментарий");
        return false;
      } finally {
        setSendingComment(false);
      }
    },
    [
      book.id,
      canSetRatingOnNewReview,
      isAuthenticated,
      loadComments,
      navigate,
      sendingComment,
      toast,
    ]
  );

  const handleSubmitComment = React.useCallback(
    async (event) => {
      event.preventDefault();
      const ok = await submitCommentPayload(commentText, null, commentRating);
      if (!ok) return;
      setCommentText("");
      setCommentRating(0);
    },
    [commentRating, commentText, submitCommentPayload]
  );

  const handleSubmitReply = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (!replyTo?.id) return;
      const ok = await submitCommentPayload(replyText, replyTo.id);
      if (!ok) return;
      setReplyText("");
      setReplyTo(null);
    },
    [replyText, replyTo, submitCommentPayload]
  );

  const beginEditComment = React.useCallback((comment) => {
    if (!comment?.id) return;
    setEditingComment({
      id: comment.id,
      parentCommentId: comment.parentCommentId ?? null,
      existingRating: normalizeStarRating(comment.rating),
    });
    setEditingText(String(comment.content || ""));
    setEditingRating(normalizeStarRating(comment.rating));
    setReplyTo(null);
    setReplyText("");
  }, []);

  const cancelEditComment = React.useCallback(() => {
    setEditingComment(null);
    setEditingText("");
    setEditingRating(0);
  }, []);

  const submitEditComment = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (editingBusy) return;
      const commentId = editingComment?.id;
      if (!commentId) return;
      const normalizedContent = String(editingText || "").trim();
      if (!normalizedContent) {
        toast.info("Введите текст отзыва");
        return;
      }
      if (!isAuthenticated) {
        toast.info("Редактирование доступно после авторизации");
        navigate("/login");
        return;
      }

      const existingRating = normalizeStarRating(editingComment?.existingRating);
      const nextRating = normalizeStarRating(editingRating);
      const shouldSendRating =
        editingComment?.parentCommentId == null &&
        existingRating <= 0 &&
        canSetRatingOnNewReview &&
        nextRating > 0;

      setEditingBusy(true);
      try {
        await literatureApi.updateComment(commentId, {
          parentCommentId: editingComment?.parentCommentId ?? null,
          content: normalizedContent,
          rating: shouldSendRating ? nextRating : null,
        });
        await loadComments();
        cancelEditComment();
        toast.success("Отзыв обновлен");
      } catch (errorValue) {
        toast.error(errorValue?.message || "Не удалось обновить отзыв");
      } finally {
        setEditingBusy(false);
      }
    },
    [
      cancelEditComment,
      canSetRatingOnNewReview,
      editingBusy,
      editingComment?.existingRating,
      editingComment?.id,
      editingComment?.parentCommentId,
      editingRating,
      editingText,
      isAuthenticated,
      loadComments,
      navigate,
      toast,
    ]
  );

  const goToBook = React.useCallback(
    (nextBook) => {
      const route = makeBookRoute(nextBook);
      if (!route) return;
      navigate(route);
    },
    [navigate]
  );

  const openPsychologistProfile = React.useCallback(
    (event, userId, profileUrl = "") => {
      const route = resolvePsychologistProfileRoute(profileUrl, userId);
      if (!route) return;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
      }
      if (isExternalHttpUrl(route) && typeof window !== "undefined") {
        window.location.assign(route);
        return;
      }
      navigate(route);
    },
    [navigate]
  );

  const externalStoreLink = sanitizeLinkHref(book.externalStore.url || "", {
    allowRelative: false,
    allowMailto: false,
    allowTel: false,
    allowHash: false,
  });
  const buyLink = sanitizeLinkHref(book.buyUrl || book.externalStore.url || "", {
    allowRelative: false,
    allowMailto: false,
    allowTel: false,
    allowHash: false,
  });

  const readLink = bookFileUrl || buyLink || externalStoreLink || "";
  const basePrice = React.useMemo(() => {
    if (book.price != null) return book.price;
    const externalPrice = parsePriceNumber(book.externalStore?.displayText);
    return externalPrice;
  }, [book.externalStore?.displayText, book.price]);
  const canBuy = Boolean(book.id);
  const hasExplicitPrice = basePrice != null;
  const isFreeBook = (hasExplicitPrice && safeNumber(basePrice, 0) <= 0) || (!hasExplicitPrice && Boolean(book.hasReadingAccess && bookFileUrl));
  const isPaidBook = hasExplicitPrice && safeNumber(basePrice, 0) > 0;
  const hasReadingAccess = Boolean(book.hasReadingAccess || book.isPurchasedByMe || isFreeBook);
  const showBuyControls = canBuy && isPaidBook;
  const discountPercent = Math.max(0, Math.min(99, safeNumber(book.discountPercent, 0)));
  const hasDiscount = isPaidBook && discountPercent > 0;
  const oldPriceValue = hasDiscount
    ? Number((safeNumber(basePrice, 0) / (1 - discountPercent / 100)).toFixed(2))
    : null;
  const oldPriceLabel =
    oldPriceValue != null ? formatMoney(oldPriceValue, book.priceCurrency || "RUB") : "";
  const priceLabel = isFreeBook ? "Бесплатно" : basePrice != null ? formatMoney(basePrice, book.priceCurrency || "RUB") : "Цена не указана";
  const canRecommendBook = role === "PSYCHOLOGIST" || role === "ADMIN" || role === "MODERATOR";

  const chapterList = React.useMemo(() => buildChapters(book, readProgress), [book, readProgress]);
  const descriptionParagraphs = React.useMemo(() => {
    const fromDescription = splitDescriptionToParagraphs(book.description);
    if (fromDescription.length) return fromDescription;
    const fromShort = splitDescriptionToParagraphs(book.shortDescription);
    if (fromShort.length) return fromShort;
    return [];
  }, [book.description, book.shortDescription]);

  const reviewsCount = React.useMemo(
    () => asArray(comments).filter((comment) => comment.parentCommentId == null).length,
    [comments]
  );
  const readersCount = Math.max(
    0,
    safeNumber(book.readersCount, 0),
    safeNumber(recommendationsTotal, 0),
    safeNumber(book.recommendationsCount, 0),
    recommendations.length
  );
  const estimatedHours = estimateReadHours(book.pagesCount);
  const currentChapter = Math.max(
    1,
    Math.min(chapterList.length || 1, Math.ceil((readProgress / 100) * (chapterList.length || 1)))
  );
  const approximateMinutesLeft = Math.max(
    1,
    Math.round(((Math.max(1, safeNumber(book.pagesCount, 180)) / 1.8) * (100 - readProgress)) / 100)
  );
  const ratedTopLevelComments = React.useMemo(
    () =>
      comments.filter(
        (comment) =>
          comment.parentCommentId == null &&
          normalizeStarRating(comment.rating) > 0
      ),
    [comments]
  );
  const commentsRatingStats = React.useMemo(() => {
    const votes = ratedTopLevelComments.length;
    if (!votes) return { avg: 0, votes: 0 };
    const total = ratedTopLevelComments.reduce(
      (sum, comment) => sum + normalizeStarRating(comment.rating),
      0
    );
    return { avg: total / votes, votes };
  }, [ratedTopLevelComments]);
  const ratingValue = React.useMemo(() => {
    if (commentsRatingStats.votes > 0) return commentsRatingStats.avg;
    return Math.max(0, safeNumber(book.averageRating, 0));
  }, [book.averageRating, commentsRatingStats.avg, commentsRatingStats.votes]);
  const ratingVotes = commentsRatingStats.votes || Math.max(0, safeNumber(book.ratingsCount, 0));
  const ratingLabel = ratingValue > 0 ? ratingValue.toFixed(1) : "—";
  const filledStars = Math.max(0, Math.min(5, Math.round(ratingValue)));
  const hasStartedReading = readProgress > 0;
  const readCtaLabel = hasReadingAccess
    ? hasStartedReading
      ? "Продолжить чтение"
      : "Начать читать"
    : showBuyControls
      ? "Купить чтобы читать"
      : "Чтение недоступно";
  React.useEffect(() => {
    if (canSetRatingOnNewReview) return;
    if (commentRating !== 0) setCommentRating(0);
  }, [canSetRatingOnNewReview, commentRating]);
  const bookReactionButtons = React.useMemo(
    () => buildBookReactionButtons(bookReactionState, safeNumber(book.recommendationsCount, 0)),
    [book.recommendationsCount, bookReactionState]
  );

  const badges = React.useMemo(() => {
    const output = [];
    if (readersCount >= 100) output.push({ label: "Бестселлер", tone: "mint" });
    if (book.difficultyLabel) output.push({ label: book.difficultyLabel, tone: "lav" });
    if (book.categoryLabel) output.push({ label: book.categoryLabel, tone: "grey" });
    if (!output.length && book.topics.length) output.push({ label: book.topics[0], tone: "lav" });
    if (output.length < 3 && book.whoFor.length) output.push({ label: book.whoFor[0], tone: "grey" });
    if (output.length < 3 && book.language) output.push({ label: book.language, tone: "grey" });
    return output.slice(0, 3);
  }, [book.categoryLabel, book.difficultyLabel, book.language, book.topics, book.whoFor, readersCount]);

  const shownComments = React.useMemo(
    () => flattenComments(comments, { sort: reviewsSort }),
    [comments, reviewsSort]
  );

  const relatedBooks = React.useMemo(
    () =>
      uniqueBy(similarBooks, (item) => String(item?.id ?? item?.slug ?? item?.title ?? ""))
        .filter((item) => String(item?.id || item?.slug || "") !== String(book.id || book.slug || ""))
        .slice(0, 4),
    [book.id, book.slug, similarBooks]
  );
  const canOpenBookFile = Boolean(bookFileUrl);
  const isPdfReader = readEmbedMode === "pdf" && canOpenBookFile;

  const ringCircumference = 2 * Math.PI * 18;
  const ringOffset = ringCircumference - (readProgress / 100) * ringCircumference;
  const commentReplyLabel = replyTo ? `Ответ: ${replyTo.authorName}` : "Новый отзыв";

  const closeReadModal = React.useCallback(() => setReadModalOpen(false), []);
  const closeBuyModal = React.useCallback(() => setBuyModalOpen(false), []);
  const closeRecommendModal = React.useCallback(() => setRecommendModalOpen(false), []);
  const handleOpenReadModal = React.useCallback(() => {
    if (hasReadingAccess) {
      setReadModalOpen(true);
      return;
    }
    if (showBuyControls) {
      setBuyModalOpen(true);
      toast.info("Для чтения сначала оплатите книгу");
      return;
    }
    toast.info("Книга пока недоступна для чтения");
  }, [hasReadingAccess, showBuyControls, toast]);

  const onReadBodyScroll = React.useCallback(
    (event) => {
      const target = event.currentTarget;
      const max = target.scrollHeight - target.clientHeight;
      if (max <= 0) return;
      const rawPct = Math.round((target.scrollTop / max) * 100);
      if (!Number.isFinite(rawPct)) return;
      const next = clamp(Math.max(readProgress, rawPct), 0, 100);
      if (next === readProgress) return;
      updateReadProgress(next);
    },
    [readProgress, updateReadProgress]
  );

  const handleManualProgressChange = React.useCallback(
    (event) => {
      const rawNext = Number(event.target.value);
      const normalized = clamp(rawNext, 0, 100);
      if (isPdfReader && pdfPagesTotal > 0) {
        const nextPage = clamp(
          Math.round((normalized / 100) * pdfPagesTotal) || 1,
          1,
          pdfPagesTotal
        );
        setPdfCurrentPage(nextPage);
        return;
      }
      updateReadProgress(normalized);
    },
    [isPdfReader, pdfPagesTotal, updateReadProgress]
  );

  const handlePdfPageStep = React.useCallback(
    (step) => {
      if (!pdfPagesTotal) return;
      setPdfCurrentPage((prev) => clamp(prev + step, 1, pdfPagesTotal));
    },
    [pdfPagesTotal]
  );

  const handlePdfPageInput = React.useCallback(
    (event) => {
      const raw = Number(event.target.value);
      if (!Number.isFinite(raw) || !pdfPagesTotal) return;
      setPdfCurrentPage(clamp(Math.round(raw), 1, pdfPagesTotal));
    },
    [pdfPagesTotal]
  );

  React.useEffect(() => {
    if (!readModalOpen || !isPdfReader) return undefined;
    if (typeof document === "undefined") return undefined;
    const onKeyDown = (event) => {
      const targetTag = String(event.target?.tagName || "").toUpperCase();
      if (targetTag === "INPUT" || targetTag === "TEXTAREA" || targetTag === "SELECT") return;
      if (event.key === "ArrowLeft" || event.key === "PageUp") {
        event.preventDefault();
        handlePdfPageStep(-1);
      }
      if (event.key === "ArrowRight" || event.key === "PageDown") {
        event.preventDefault();
        handlePdfPageStep(1);
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [handlePdfPageStep, isPdfReader, readModalOpen]);

  const handleShareBook = React.useCallback(async () => {
    const url = String(shareLink || "").trim();
    if (!url) {
      toast.info("Ссылка на книгу пока недоступна");
      return;
    }
    const title = String(book.shareTitle || book.title || "Книга").trim();
    const text = String(
      book.shareDescription ||
        book.shortDescription ||
        (book.title ? `Рекомендую книгу «${book.title}»` : "Рекомендую книгу")
    ).trim();

    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        const payload = {
          title: title || "Книга",
          text,
          url,
        };
        if (typeof navigator.canShare !== "function" || navigator.canShare(payload)) {
          await navigator.share(payload);
          toast.success("Книга отправлена");
          return;
        }
      } catch (errorValue) {
        if (errorValue?.name === "AbortError") return;
      }
    }

    const fallbackCopy = async () => {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
        return true;
      }
      if (typeof document !== "undefined") {
        const input = document.createElement("textarea");
        input.value = url;
        input.setAttribute("readonly", "");
        input.style.position = "fixed";
        input.style.opacity = "0";
        document.body.appendChild(input);
        input.select();
        const copied = document.execCommand("copy");
        document.body.removeChild(input);
        return copied;
      }
      return false;
    };

    try {
      const copied = await fallbackCopy();
      if (!copied) throw new Error("COPY_UNAVAILABLE");
      toast.success("Ссылка на книгу скопирована");
    } catch {
      toast.info("Не удалось открыть меню шаринга или скопировать ссылку");
    }
  }, [book.shareDescription, book.shareTitle, book.shortDescription, book.title, shareLink, toast]);

  const handleOpenRecommendModal = React.useCallback(() => {
    if (!isAuthenticated) {
      toast.info("Рекомендация доступна после авторизации");
      navigate("/login");
      return;
    }
    if (!canRecommendBook) {
      toast.info("Рекомендовать книгу могут только специалисты");
      return;
    }
    setRecommendModalOpen(true);
  }, [canRecommendBook, isAuthenticated, navigate, toast]);

  const handleSubmitRecommendation = React.useCallback(
    async (event) => {
      event.preventDefault();
      if (!book.id) return;
      if (!recommendRecipient?.userId) {
        toast.info("Выберите пользователя для рекомендации");
        return;
      }
      if (recommendSending) return;

      setRecommendSending(true);
      try {
        await literatureApi.recommendBook(book.id, {
          recipientUserId: recommendRecipient.userId,
          comment: recommendComment,
          sendNotification: true,
          duplicateToChat: true,
          openOrCreateDialog: true,
        });
        await reloadBookRealtime();
        closeRecommendModal();
        toast.success("Рекомендация отправлена: уведомление и сообщение в чат созданы");
      } catch (errorValue) {
        toast.error(errorValue?.message || "Не удалось отправить рекомендацию");
      } finally {
        setRecommendSending(false);
      }
    },
    [
      book.id,
      closeRecommendModal,
      recommendComment,
      recommendRecipient?.userId,
      recommendSending,
      reloadBookRealtime,
      toast,
    ]
  );

  const handleBuySubmit = React.useCallback(async () => {
    if (buySubmitting || !book.id) return;
    if (!isAuthenticated) {
      toast.info("Оплата доступна после авторизации");
      navigate("/login");
      return;
    }

    setBuySubmitting(true);
    try {
      const returnUrl =
        typeof window !== "undefined"
          ? `${window.location.origin}${window.location.pathname}${window.location.search}${window.location.hash}`
          : "";
      const raw = await literatureApi.startBookCheckout(book.id, { returnUrl });
      const payload =
        raw && typeof raw.item === "object" && raw.item !== null
          ? raw.item
          : raw;
      const destination = String(
        payload?.confirmationUrl ||
          payload?.confirmUrl ||
          payload?.url ||
          payload?.paymentUrl ||
          payload?.redirectUrl ||
          payload?.confirmation?.confirmation_url ||
          payload?.confirmation?.confirmationUrl ||
          payload?.payment?.confirmation?.confirmation_url ||
          payload?.payment?.confirmation?.confirmationUrl ||
          ""
      ).trim();

      if (!destination) {
        throw new Error("CHECKOUT_URL_MISSING");
      }

      closeBuyModal();
      if (typeof window !== "undefined") {
        window.location.assign(destination);
      }
    } catch (errorValue) {
      toast.error(errorValue?.message || "Не удалось создать оплату книги");
    } finally {
      setBuySubmitting(false);
    }
  }, [book.id, buySubmitting, closeBuyModal, isAuthenticated, navigate, toast]);

  const handleSearchSubmit = React.useCallback(
    (event) => {
      event.preventDefault();
      const value = String(searchInput || "").trim();
      navigate(`/literature${value ? `?q=${encodeURIComponent(value)}` : ""}`);
    },
    [navigate, searchInput]
  );

  const activateTab = React.useCallback(
    (nextTab) => {
      const tabKey = ["about", "contents", "reviews"].includes(nextTab) ? nextTab : "about";
      setActiveTab(tabKey);
      const nextHash = TAB_HASH_MAP[tabKey] || TAB_HASH_MAP.about;
      if (location.hash === nextHash) return;
      navigate(
        {
          pathname: location.pathname,
          search: location.search,
          hash: nextHash,
        },
        { replace: true }
      );
    },
    [location.hash, location.pathname, location.search, navigate]
  );

  return (
    <div className="podcasts-hub literature-hub-shell literature-detail-shell-v2">
      <Helmet prioritizeSeoTags>
        <title>{book.title ? `${book.title} | Литература | Бюро Души` : "Литература | Бюро Души"}</title>
        <meta
          name="description"
          content={book.shortDescription || "Детальный просмотр книги в разделе литературы Бюро Души."}
        />
        {canonical ? <link rel="canonical" href={canonical} /> : null}
      </Helmet>
      <BreadcrumbsSchema items={breadcrumbs} />

      <div className="podcasts-shell">
        <AppSidebarV2
          activeKey="literature"
          mobileOpen={sidebarOpen}
          onCloseMobile={() => setSidebarOpen(false)}
          userName={me?.name || me?.login || "Гость"}
          roleLabel={roleLabel}
          canOpenLiteratureEditor={canManageBooks}
          notificationsBadge={unreadNotificationsCount}
          onLogout={logout}
        />

        <button
          className={`podcasts-overlay ${sidebarOpen ? "is-open" : ""}`}
          type="button"
          aria-label="Закрыть меню"
          onClick={() => setSidebarOpen(false)}
        />

        <div className="podcasts-main">
          <header className="podcasts-topbar lit-book-view__topbar">
            <div className="podcasts-topbar__left">
              <button
                className="podcasts-topbar__menu-btn"
                type="button"
                onClick={() => setSidebarOpen(true)}
                aria-label="Открыть меню"
              >
                ☰
              </button>
              <div className="lit-book-view__breadcrumb">
                <button type="button" onClick={() => navigate("/literature")}>
                  Литература
                </button>
                <span>›</span>
                <button type="button" onClick={() => navigate("/literature")}>
                  Каталог
                </button>
                <span>›</span>
                <strong>{book.title || "Книга"}</strong>
              </div>
            </div>
            <div className="podcasts-topbar__right">
              {showBuyControls ? (
                <button
                  type="button"
                  className="lit-book-view__topbar-btn is-ghost"
                  onClick={() => setBuyModalOpen(true)}
                >
                  {`Купить — ${priceLabel}`}
                </button>
              ) : null}
              <button
                type="button"
                className="lit-book-view__topbar-btn is-primary"
                onClick={handleOpenReadModal}
              >
                {readCtaLabel}
              </button>
              <button className="podcasts-topbar__avatar" type="button" onClick={() => navigate("/dashboard/settings")}>
                {String(me?.name || me?.login || "BS").trim().slice(0, 2).toUpperCase()}
              </button>
            </div>
          </header>

          <main className="lit-book-view">
            <section className="lit-book-view__content-main">
              <section className="lit-book-view__crumbs">
                <Breadcrumbs items={breadcrumbs} />
              </section>

              <form className="lit-book-view__search" onSubmit={handleSearchSubmit}>
                <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="8.5" cy="8.5" r="5.5" />
                  <path d="M13 13l4 4" strokeLinecap="round" />
                </svg>
                <input
                  type="search"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  placeholder="Найти другую книгу..."
                />
              </form>

              {loading ? <div className="lit-book-view__state">Загрузка книги…</div> : null}
              {!loading && error ? <div className="lit-book-view__state is-error">{error}</div> : null}

              {!loading && !error ? (
                <>
                  <section className="lit-book-view__hero" id="book-about">
                    <div className="lit-book-view__cover-wrap">
                      <div className="lit-book-view__cover">
                        {book.coverUrl ? (
                          <img src={book.coverUrl} alt={book.title} loading="lazy" />
                        ) : (
                          <div className="lit-book-view__cover-fallback">
                            <div className="lit-book-view__cover-fallback-title">
                              {String(book.title || "Книга")
                                .split(/\s+/)
                                .slice(0, 3)
                                .join(" ")}
                            </div>
                            <div className="lit-book-view__cover-fallback-author">
                              {book.authorUserId || book.authorProfileUrl ? (
                                <button
                                  type="button"
                                  className="b-psych-link-btn"
                                  onClick={(event) =>
                                    openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                  }
                                >
                                  {book.author}
                                </button>
                              ) : (
                                book.author
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="lit-book-view__ring">
                        <svg width="44" height="44" viewBox="0 0 44 44" aria-hidden="true">
                          <circle className="lit-book-view__ring-bg" cx="22" cy="22" r="18" />
                          <circle
                            className="lit-book-view__ring-fill"
                            cx="22"
                            cy="22"
                            r="18"
                            strokeDasharray={ringCircumference}
                            strokeDashoffset={ringOffset}
                            transform="rotate(-90 22 22)"
                          />
                        </svg>
                        <div className="lit-book-view__ring-label">{readProgress}%</div>
                      </div>
                    </div>

                    <div className="lit-book-view__hero-info">
                      <div className="lit-book-view__badges">
                        {badges.map((badge, index) => (
                          <span key={`${badge.label}-${index}`} className={`lit-book-view__badge is-${badge.tone}`}>
                            {badge.label}
                          </span>
                        ))}
                      </div>
                      <h1 className="lit-book-view__title">{book.title}</h1>
                      <p className="lit-book-view__author">
                        Автор:{" "}
                        <span>
                          {book.authorUserId || book.authorProfileUrl ? (
                            <button
                              type="button"
                              className="b-psych-link-btn"
                              onClick={(event) =>
                                openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                              }
                            >
                              {book.author}
                            </button>
                          ) : (
                            book.author
                          )}
                        </span>
                        {book.publishYear ? ` · ${book.publishYear}` : ""}
                      </p>

                      <div className="lit-book-view__rating">
                        <div className="lit-book-view__stars" aria-hidden="true">
                          {Array.from({ length: 5 }).map((_, index) => (
                            <span key={`star-${index}`} className={index < filledStars ? "is-active" : ""}>
                              ★
                            </span>
                          ))}
                        </div>
                        <span className="lit-book-view__rating-value">{ratingLabel}</span>
                        <span className="lit-book-view__rating-count">
                          {formatNumber(ratingVotes || reviewsCount)} оценок
                        </span>
                      </div>

                      <div className="lit-book-view__metrics">
                        <div className="lit-book-view__metric">
                          <div className="lit-book-view__metric-value is-mint">{formatNumber(readersCount)}</div>
                          <div className="lit-book-view__metric-label">Читателей</div>
                        </div>
                        <div className="lit-book-view__metric">
                          <div className="lit-book-view__metric-value">{book.pagesCount || "—"}</div>
                          <div className="lit-book-view__metric-label">Страниц</div>
                        </div>
                        <div className="lit-book-view__metric">
                          <div className="lit-book-view__metric-value is-lav">
                            {estimatedHours ? `~${estimatedHours} ч` : "—"}
                          </div>
                          <div className="lit-book-view__metric-label">Чтение</div>
                        </div>
                        <div className="lit-book-view__metric">
                          <div className="lit-book-view__metric-value">{book.language || "—"}</div>
                          <div className="lit-book-view__metric-label">Язык</div>
                        </div>
                      </div>

                      <div className="lit-book-view__actions">
                        <button type="button" className="lit-book-view__btn is-primary" onClick={handleOpenReadModal}>
                          {readCtaLabel}
                        </button>
                        {showBuyControls ? (
                          <button
                            type="button"
                            className="lit-book-view__btn is-dark"
                            onClick={() => setBuyModalOpen(true)}
                          >
                            {`Купить — ${priceLabel}`}
                          </button>
                        ) : null}
                        {canRecommendBook ? (
                          <button
                            type="button"
                            className="lit-book-view__btn is-ghost"
                            onClick={handleOpenRecommendModal}
                          >
                            Рекомендовать книгу
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className={`lit-book-view__icon-btn ${book.saved ? "is-active" : ""}`}
                          onClick={handleToggleSave}
                          disabled={savingBook}
                          title={book.saved ? "Убрать из сохранённых" : "Сохранить"}
                        >
                          {book.saved ? "🔖" : "📑"}
                        </button>
                        <button
                          type="button"
                          className="lit-book-view__icon-btn"
                          onClick={handleShareBook}
                          title="Поделиться"
                        >
                          ⤴
                        </button>
                        <ReportContentButton
                          targetType="LITERATURE_BOOK"
                          targetId={book.id}
                          title={book.title}
                          contentSnippet={book.description}
                          className="lit-book-view__icon-btn lit-book-view__icon-btn--report"
                        >
                          ⚑
                        </ReportContentButton>
                      </div>
                    </div>
                  </section>

                  <section className="lit-book-view__progress">
                    <div className="lit-book-view__progress-head">
                      <div>Прогресс чтения</div>
                      <div>{readProgress}%</div>
                    </div>
                    <div className="lit-book-view__progress-track">
                      <div className="lit-book-view__progress-fill" style={{ width: `${readProgress}%` }} />
                    </div>
                    <div className="lit-book-view__progress-meta">
                      <span>
                        Глава {currentChapter} из {chapterList.length} ·{" "}
                        {book.pagesCount
                          ? `стр. ${Math.round((readProgress / 100) * book.pagesCount)} из ${book.pagesCount}`
                          : "без пагинации"}
                      </span>
                      <span>~{approximateMinutesLeft} мин осталось</span>
                    </div>
                    <div className="lit-book-view__chapter-dots">
                      {chapterList.map((chapter) => (
                        <button
                          key={`dot-${chapter.num}`}
                          type="button"
                          className={`lit-book-view__chapter-dot is-${chapter.status}`}
                          title={`Глава ${chapter.num}: ${chapter.title}`}
                          onClick={handleOpenReadModal}
                        />
                      ))}
                    </div>
                  </section>

                  <section className="lit-book-view__tabs">
                    <button
                      type="button"
                      className={activeTab === "about" ? "is-active" : ""}
                      onClick={() => activateTab("about")}
                    >
                      О книге
                    </button>
                    <button
                      type="button"
                      className={activeTab === "contents" ? "is-active" : ""}
                      onClick={() => activateTab("contents")}
                    >
                      Содержание
                    </button>
                    <button
                      type="button"
                      className={activeTab === "reviews" ? "is-active" : ""}
                      onClick={() => activateTab("reviews")}
                    >
                      Отзывы <span>{formatNumber(reviewsCount)}</span>
                    </button>
                  </section>

                  {activeTab === "about" ? (
                    <section className="lit-book-view__panel" id="book-panel-about">
                      <div className="lit-book-view__desc">
                        {descriptionParagraphs.length ? (
                          descriptionParagraphs.map((paragraph, index) => (
                            <p key={`desc-${index}`}>{paragraph}</p>
                          ))
                        ) : (
                          <p>Описание книги пока не заполнено.</p>
                        )}
                      </div>

                      <div className="lit-book-view__reactions">
                        <h3>Реакции читателей</h3>
                        <div className="lit-book-view__reaction-row">
                          {bookReactionButtons.map((reaction) => (
                            <button
                              key={reaction.type}
                              type="button"
                              className={`lit-book-view__reaction-btn ${reaction.active ? `is-active is-active-${reaction.style}` : ""}`.trim()}
                              onClick={(event) => handleToggleBookReaction(event, reaction.type)}
                              disabled={likingBook}
                              title={reaction.label || reaction.type}
                            >
                              <span>{reaction.emoji}</span>
                              <b>{formatNumber(reaction.count)}</b>
                            </button>
                          ))}
                        </div>
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "contents" ? (
                    <section className="lit-book-view__panel" id="book-contents">
                      <div className="lit-book-view__toc">
                        {chapterList.map((chapter) => (
                          <button
                            key={`toc-${chapter.num}`}
                            type="button"
                            className={`lit-book-view__toc-item is-${chapter.status}`}
                            onClick={handleOpenReadModal}
                          >
                            <span className="lit-book-view__toc-num">{String(chapter.num).padStart(2, "0")}</span>
                            <span className="lit-book-view__toc-title">{chapter.title}</span>
                            <span className="lit-book-view__toc-pages">{chapter.pagesLabel}</span>
                            <span className="lit-book-view__toc-status" />
                          </button>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {activeTab === "reviews" ? (
                    <section className="lit-book-view__panel" id="book-reviews">
                      <form className="lit-book-view__comment-add" onSubmit={handleSubmitComment}>
                        <div className="lit-book-view__comment-avatar">{initials(me?.name || me?.login || "Вы")}</div>
                        <div className="lit-book-view__comment-add-main">
                          <label htmlFor="lit-new-comment">{commentReplyLabel}</label>
                          <textarea
                            id="lit-new-comment"
                            rows={3}
                            value={commentText}
                            onChange={(event) => setCommentText(event.target.value)}
                            placeholder="Поделитесь впечатлением от книги…"
                          />
                          <div className="lit-book-view__comment-add-foot">
                            <div className="lit-book-view__comment-tools">
                              {Array.from({ length: 5 }).map((_, index) => {
                                const star = index + 1;
                                return (
                                  <button
                                    key={`new-rating-${star}`}
                                    type="button"
                                    className={`${commentRating >= star ? "is-active-star" : ""} ${!canSetRatingOnNewReview ? "is-disabled" : ""}`.trim()}
                                    onClick={() => {
                                      if (!canSetRatingOnNewReview) return;
                                      setCommentRating(star);
                                    }}
                                    disabled={!canSetRatingOnNewReview}
                                    title={`Оценка ${star}`}
                                    aria-label={`Оценка ${star} из 5`}
                                  >
                                    ★
                                  </button>
                                );
                              })}
                            </div>
                            <button
                              type="submit"
                              className="lit-book-view__btn is-primary is-compact"
                              disabled={sendingComment || !String(commentText).trim()}
                            >
                              Опубликовать отзыв
                            </button>
                          </div>
                          {!canSetRatingOnNewReview ? (
                            <small className="lit-book-view__state">
                              Ваша оценка уже выставлена: {"★".repeat(normalizeStarRating(myRatedReview?.rating))} ({normalizeStarRating(myRatedReview?.rating)}/5)
                            </small>
                          ) : null}
                        </div>
                      </form>

                      <div className="lit-book-view__comments-head">
                        <h3>{formatNumber(reviewsCount)} отзывов</h3>
                        <div className="lit-book-view__comments-sort">
                          <button
                            type="button"
                            className={reviewsSort === "new" ? "is-active" : ""}
                            onClick={() => setReviewsSort("new")}
                          >
                            Новые
                          </button>
                          <button
                            type="button"
                            className={reviewsSort === "popular" ? "is-active" : ""}
                            onClick={() => setReviewsSort("popular")}
                          >
                            Популярные
                          </button>
                        </div>
                      </div>

                      {commentsLoading ? (
                        <div className="lit-book-view__state">Загрузка комментариев…</div>
                      ) : null}
                      {!commentsLoading && !shownComments.length ? (
                        <div className="lit-book-view__state">Пока нет комментариев</div>
                      ) : null}

                      {!commentsLoading && shownComments.length ? (
                        <div className="lit-book-view__comments">
                          {shownComments.map((comment, index) => {
                            const isReplyOpen = replyTo?.id === comment.id;
                            const level = Math.max(0, Math.min(6, safeNumber(comment.level, 0)));
                            const starsCount = normalizeStarRating(comment.rating);
                            const isOwnComment =
                              myUserId != null && parsePositiveId(comment.authorUserId) === myUserId;
                            const isEditing =
                              editingComment?.id != null &&
                              String(editingComment.id) === String(comment.id);
                            const hasEditedMark = isEditedTimestamp(comment.createdWhen, comment.updatedWhen);
                            const authorBadge =
                              String(comment.authorName || "").trim().toLowerCase() ===
                              String(book.author || "").trim().toLowerCase();
                            const canEditRatingOnThisComment =
                              isEditing &&
                              comment.parentCommentId == null &&
                              normalizeStarRating(editingComment?.existingRating) <= 0 &&
                              canSetRatingOnNewReview;
                            return (
                              <article
                                key={`comment-${comment.id || index}-${comment.createdWhen || "time"}`}
                                className="lit-book-view__comment"
                                style={{ marginLeft: `${level * COMMENT_LEVEL_INDENT}px` }}
                              >
                                <div className="lit-book-view__comment-avatar">
                                  {initials(comment.authorName)}
                                </div>
                                <div className="lit-book-view__comment-body">
                                  <div className="lit-book-view__comment-meta">
                                    {comment.authorUserId ? (
                                      <button
                                        type="button"
                                        className="b-psych-link-btn"
                                        onClick={(event) =>
                                          openPsychologistProfile(event, comment.authorUserId, "")
                                        }
                                      >
                                        <strong>{comment.authorName || "Пользователь"}</strong>
                                      </button>
                                    ) : (
                                      <strong>{comment.authorName || "Пользователь"}</strong>
                                    )}
                                    {authorBadge ? <span className="is-author">Автор</span> : null}
                                    {starsCount > 0 ? (
                                      <span className="lit-book-view__comment-rating">
                                        {"★".repeat(starsCount)} <em>({starsCount}/5)</em>
                                      </span>
                                    ) : null}
                                    <time>
                                      {formatDateTime(comment.createdWhen)}
                                      {hasEditedMark ? (
                                        <span className="lit-book-view__comment-edited">
                                          {" "}
                                          · ред. {formatDateTime(comment.updatedWhen)}
                                        </span>
                                      ) : null}
                                    </time>
                                  </div>
                                  {isEditing ? (
                                    <form className="lit-book-view__comment-edit" onSubmit={submitEditComment}>
                                      <textarea
                                        rows={3}
                                        value={editingText}
                                        onChange={(event) => setEditingText(event.target.value)}
                                        placeholder="Отредактируйте отзыв…"
                                      />
                                      <div className="lit-book-view__comment-edit-foot">
                                        {canEditRatingOnThisComment ? (
                                          <div className="lit-book-view__comment-tools">
                                            {Array.from({ length: 5 }).map((_, index) => {
                                              const star = index + 1;
                                              return (
                                                <button
                                                  key={`edit-rating-${comment.id}-${star}`}
                                                  type="button"
                                                  className={editingRating >= star ? "is-active-star" : ""}
                                                  onClick={() => setEditingRating(star)}
                                                  title={`Оценка ${star}`}
                                                  aria-label={`Оценка ${star} из 5`}
                                                >
                                                  ★
                                                </button>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span />
                                        )}
                                        <div className="lit-book-view__comment-edit-actions">
                                          <button
                                            type="button"
                                            className="lit-book-view__btn is-ghost is-compact"
                                            onClick={cancelEditComment}
                                            disabled={editingBusy}
                                          >
                                            Отмена
                                          </button>
                                          <button
                                            type="submit"
                                            className="lit-book-view__btn is-primary is-compact"
                                            disabled={editingBusy || !String(editingText).trim()}
                                          >
                                            Сохранить
                                          </button>
                                        </div>
                                      </div>
                                    </form>
                                  ) : (
                                    <>
                                      <p>{comment.content || "Комментарий без текста."}</p>
                                      <div className="lit-book-view__comment-actions">
                                        <button
                                          type="button"
                                          className={comment.likedByMe ? "is-liked" : ""}
                                          onClick={(event) => handleToggleCommentReaction(event, comment.id)}
                                          disabled={reactionBusy.has(String(comment.id))}
                                        >
                                          ❤️ {formatNumber(comment.reactionsCount)}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setReplyTo(isReplyOpen ? null : comment);
                                            setReplyText("");
                                          }}
                                        >
                                          ↩ Ответить
                                        </button>
                                        {isOwnComment ? (
                                          <button type="button" onClick={() => beginEditComment(comment)}>
                                            ✎ Редактировать
                                          </button>
                                        ) : (
                                          <ReportContentButton
                                            targetType="LITERATURE_COMMENT"
                                            targetId={comment.id}
                                            title={book?.title || "Комментарий к книге"}
                                            contentSnippet={comment.content}
                                            className="is-danger"
                                            compact
                                          />
                                        )}
                                      </div>

                                      {isReplyOpen ? (
                                        <form className="lit-book-view__reply-form" onSubmit={handleSubmitReply}>
                                          <textarea
                                            rows={2}
                                            value={replyText}
                                            onChange={(event) => setReplyText(event.target.value)}
                                            placeholder={`Ответ для ${comment.authorName}`}
                                          />
                                          <div>
                                            <button
                                              type="button"
                                              className="lit-book-view__btn is-ghost is-compact"
                                              onClick={() => {
                                                setReplyTo(null);
                                                setReplyText("");
                                              }}
                                            >
                                              Отмена
                                            </button>
                                            <button
                                              type="submit"
                                              className="lit-book-view__btn is-primary is-compact"
                                              disabled={sendingComment || !String(replyText).trim()}
                                            >
                                              Ответить
                                            </button>
                                          </div>
                                        </form>
                                      ) : null}
                                    </>
                                  )}
                                </div>
                              </article>
                            );
                          })}
                        </div>
                      ) : null}
                    </section>
                  ) : null}
                </>
              ) : null}
            </section>

            <aside className="lit-book-view__content-aside">
              {!loading && !error ? (
                <>
                  <section className="lit-book-view__widget">
                    <header>
                      <h3>Доступ к книге</h3>
                    </header>
                    <div className="lit-book-view__widget-body">
                      <div className="lit-book-view__price-main">{priceLabel}</div>
                      {hasDiscount ? (
                        <div className="lit-book-view__price-old-row">
                          <div className="lit-book-view__price-old">{oldPriceLabel}</div>
                          <span className="lit-book-view__price-discount">{`-${discountPercent}%`}</span>
                        </div>
                      ) : null}
                      <ul className="lit-book-view__price-features">
                        <li>Полный текст книги</li>
                        {book.textVersionFormat ? <li>Формат: {book.textVersionFormat}</li> : null}
                        {book.pagesCount ? <li>{book.pagesCount} страниц</li> : null}
                        {book.language ? <li>Язык: {book.language}</li> : null}
                      </ul>
                      <button type="button" className="lit-book-view__btn is-primary is-full" onClick={handleOpenReadModal}>
                        {readCtaLabel}
                      </button>
                      {showBuyControls ? (
                        <>
                          <button
                            type="button"
                            className="lit-book-view__btn is-dark is-full"
                            onClick={() => setBuyModalOpen(true)}
                          >
                            {`Купить за ${priceLabel}`}
                          </button>
                          <small>Оплата на стороне магазина</small>
                        </>
                      ) : (
                        <small>
                          {isFreeBook
                            ? "Книга доступна бесплатно"
                            : !hasExplicitPrice
                              ? "Цена не указана — покупка недоступна"
                              : hasReadingAccess
                                ? "Доступ к чтению уже активирован"
                                : "Книга доступна после оплаты"}
                        </small>
                      )}
                    </div>
                  </section>

                  <section className="lit-book-view__widget">
                    <header>
                      <h3>Об авторе</h3>
                    </header>
                    <div className="lit-book-view__widget-body">
                      <div className="lit-book-view__author-card">
                        <div className="lit-book-view__author-avatar">{initials(book.author)}</div>
                        <div>
                          <div className="lit-book-view__author-name">
                            {book.authorUserId || book.authorProfileUrl ? (
                              <button
                                type="button"
                                className="b-psych-link-btn"
                                onClick={(event) =>
                                  openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                                }
                              >
                                {book.author}
                              </button>
                            ) : (
                              book.author
                            )}
                          </div>
                          <div className="lit-book-view__author-role">
                            {book.difficultyLabel || "Автор книги"}
                          </div>
                        </div>
                      </div>
                      <p className="lit-book-view__author-bio">
                        {book.shortDescription || "Описание автора отсутствует."}
                      </p>
                      {book.authorUserId || book.authorProfileUrl ? (
                        <button
                          type="button"
                          className="lit-book-view__follow-btn"
                          onClick={(event) =>
                            openPsychologistProfile(event, book.authorUserId, book.authorProfileUrl)
                          }
                        >
                          Открыть профиль автора
                        </button>
                      ) : null}
                    </div>
                  </section>

                  <section className="lit-book-view__widget">
                    <header>
                      <h3>Рекомендуют психологи</h3>
                    </header>
                    <div className="lit-book-view__widget-body">
                      <div className="lit-book-view__recommend-total">
                        {formatNumber(readersCount)}
                      </div>
                      {recommendationsLoading && !recommendations.length ? (
                        <div className="lit-book-view__state">Загрузка…</div>
                      ) : null}
                      {!recommendationsLoading && !recommendations.length ? (
                        <div className="lit-book-view__state">Пока нет рекомендаций</div>
                      ) : null}
                      {recommendations.length ? (
                        <div className="lit-book-view__recommend-list">
                          {recommendations.slice(0, 4).map((row, index) => (
                            <button
                              key={`rec-${row.psychologistUserId || index}`}
                              type="button"
                              onClick={(event) =>
                                openPsychologistProfile(event, row.psychologistUserId, row.profileUrl)
                              }
                            >
                              <strong>{row.psychologistName}</strong>
                              <span>{row.subtitle || row.psychologistHeadline || "Специалист платформы"}</span>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </section>

                  <section className="lit-book-view__widget">
                    <header>
                      <h3>Другие книги автора</h3>
                    </header>
                    <div className="lit-book-view__widget-body">
                      {similarLoading && !relatedBooks.length ? (
                        <div className="lit-book-view__state">Загрузка подборки…</div>
                      ) : null}
                      {!similarLoading && !relatedBooks.length ? (
                        <div className="lit-book-view__state">Список пока пуст</div>
                      ) : null}
                      {relatedBooks.length ? (
                        <div className="lit-book-view__related-list">
                          {relatedBooks.map((similarBook, index) => (
                            <button
                              key={`related-${similarBook.id || similarBook.slug || index}`}
                              type="button"
                              className="lit-book-view__related-item"
                              onClick={() => goToBook(similarBook)}
                            >
                              <div className="lit-book-view__related-cover">
                                {similarBook.coverUrl ? <img src={similarBook.coverUrl} alt={similarBook.title} loading="lazy" /> : null}
                              </div>
                              <div className="lit-book-view__related-info">
                                <strong>{similarBook.title}</strong>
                                <span>
                                  {similarBook.authorUserId || similarBook.authorProfileUrl ? (
                                    <button
                                      type="button"
                                      className="b-psych-link-btn"
                                      onClick={(event) =>
                                        openPsychologistProfile(
                                          event,
                                          similarBook.authorUserId,
                                          similarBook.authorProfileUrl
                                        )
                                      }
                                    >
                                      {similarBook.author}
                                    </button>
                                  ) : (
                                    similarBook.author
                                  )}
                                </span>
                              </div>
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </section>
                </>
              ) : null}
            </aside>
          </main>

          <div
            className={`lit-book-view__read-modal ${readModalOpen ? "is-open" : ""}`}
            onClick={(event) => {
              if (event.target !== event.currentTarget) return;
              closeReadModal();
            }}
          >
            <section className="lit-book-view__read-inner" aria-modal="true" role="dialog" aria-label="Режим чтения">
              <header className="lit-book-view__read-topbar">
                <button type="button" onClick={closeReadModal}>
                  ✕
                </button>
                <strong>{book.title}</strong>
                <div className="lit-book-view__read-mini-progress">
                  <div style={{ width: `${readProgress}%` }} />
                </div>
                <span>{readProgress}%</span>
              </header>

              <div className="lit-book-view__read-toolbar">
                <button
                  type="button"
                  className={readTheme === "white" ? "is-active" : ""}
                  onClick={() => setReadTheme("white")}
                >
                  ☀ Светлая
                </button>
                <button
                  type="button"
                  className={readTheme === "sepia" ? "is-active" : ""}
                  onClick={() => setReadTheme("sepia")}
                >
                  📜 Сепия
                </button>
                <button
                  type="button"
                  className={readTheme === "dark" ? "is-active" : ""}
                  onClick={() => setReadTheme("dark")}
                >
                  🌙 Тёмная
                </button>
                <span />
                <button
                  type="button"
                  className={readFontSize === 14 ? "is-active" : ""}
                  onClick={() => setReadFontSize(14)}
                >
                  A-
                </button>
                <button
                  type="button"
                  className={readFontSize === 15 ? "is-active" : ""}
                  onClick={() => setReadFontSize(15)}
                >
                  A
                </button>
                <button
                  type="button"
                  className={readFontSize === 17 ? "is-active" : ""}
                  onClick={() => setReadFontSize(17)}
                >
                  A+
                </button>
                <span />
                <button
                  type="button"
                  onClick={() => {
                    if (readLink && typeof window !== "undefined") {
                      window.open(readLink, "_blank", "noopener,noreferrer");
                    } else {
                      toast.info("Ссылка для скачивания пока недоступна");
                    }
                  }}
                >
                  ⬇ Открыть файл
                </button>
              </div>

              <div className="lit-book-view__read-progress-slider">
                <label htmlFor="lit-read-progress-range">Прогресс чтения</label>
                <input
                  id="lit-read-progress-range"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={readProgress}
                  onChange={handleManualProgressChange}
                />
                <span>{readProgress}%</span>
              </div>

              {readContentLoading ? (
                <div className="lit-book-view__state">
                  {isPdfReader ? "Загрузка PDF…" : "Загрузка текста книги…"}
                </div>
              ) : null}
              {readContentError ? <div className="lit-book-view__state">{readContentError}</div> : null}

              {isPdfReader ? (
                <section className={`lit-book-view__pdf-reader is-${readTheme}`}>
                  <div className="lit-book-view__pdf-nav">
                    <button
                      type="button"
                      onClick={() => handlePdfPageStep(-1)}
                      disabled={readContentLoading || pdfCurrentPage <= 1}
                    >
                      ← Назад
                    </button>
                    <label>
                      Стр.
                      <input
                        type="number"
                        min={1}
                        max={Math.max(1, pdfPagesTotal)}
                        value={pdfCurrentPage}
                        onChange={handlePdfPageInput}
                        disabled={readContentLoading || !pdfPagesTotal}
                      />
                      <span>/ {Math.max(1, pdfPagesTotal)}</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => handlePdfPageStep(1)}
                      disabled={readContentLoading || pdfCurrentPage >= pdfPagesTotal}
                    >
                      Вперёд →
                    </button>
                  </div>
                  <div className="lit-book-view__pdf-canvas-wrap" ref={pdfContainerRef}>
                    <canvas ref={pdfCanvasRef} className="lit-book-view__pdf-canvas" />
                  </div>
                </section>
              ) : (
                <article
                  className={`lit-book-view__read-body is-${readTheme}`}
                  style={{ "--lit-read-font-size": `${readFontSize}px` }}
                  onScroll={onReadBodyScroll}
                >
                  <h1>{book.title}</h1>
                  <p className="lit-book-view__read-meta">
                    {book.author} · Глава {currentChapter}: {chapterList[currentChapter - 1]?.title || "Чтение"}
                  </p>
                  {readParagraphs.length ? (
                    readParagraphs.map((paragraph, index) => (
                      <p key={`read-par-${index}`}>{paragraph}</p>
                    ))
                  ) : (
                    <p>Для чтения нужен загруженный файл книги. Используйте кнопку «Открыть файл».</p>
                  )}
                </article>
              )}
            </section>
          </div>

          <div
            className={`lit-book-view__recommend-modal ${recommendModalOpen ? "is-open" : ""}`}
            onClick={(event) => {
              if (event.target !== event.currentTarget) return;
              closeRecommendModal();
            }}
          >
            <section className="lit-book-view__recommend-inner" role="dialog" aria-modal="true" aria-label="Рекомендовать книгу">
              <header className="lit-book-view__buy-head">
                <h3>Рекомендовать книгу</h3>
                <button type="button" onClick={closeRecommendModal}>
                  ✕
                </button>
              </header>

              <form className="lit-book-view__recommend-form" onSubmit={handleSubmitRecommendation}>
                <label htmlFor="lit-recommend-query">Кому отправить (логин или ФИО)</label>
                <input
                  id="lit-recommend-query"
                  type="text"
                  value={recommendQuery}
                  onChange={(event) => setRecommendQuery(event.target.value)}
                  placeholder="Например: ivan.petrov или Иван Петров"
                />

                {recommendLoading ? <div className="lit-book-view__state">Идёт поиск пользователей…</div> : null}
                {recommendError ? <div className="lit-book-view__state is-error">{recommendError}</div> : null}

                {!recommendLoading && recommendQuery.trim().length >= 2 ? (
                  <div className="lit-book-view__recommend-results">
                    {recommendResults.length ? (
                      recommendResults.map((row) => {
                        const label = row.fullName || row.login || `ID ${row.userId}`;
                        const selected = row.userId === recommendRecipient?.userId;
                        return (
                          <button
                            key={`rec-user-${row.userId}`}
                            type="button"
                            className={`lit-book-view__recommend-user ${selected ? "is-selected" : ""}`}
                            onClick={() => setRecommendRecipient(row)}
                          >
                            <strong>{label}</strong>
                            <span>{row.login ? `@${row.login}` : row.role || "Пользователь"}</span>
                          </button>
                        );
                      })
                    ) : (
                      <div className="lit-book-view__state">Пользователи не найдены</div>
                    )}
                  </div>
                ) : null}

                <label htmlFor="lit-recommend-comment">Комментарий к рекомендации (опционально)</label>
                <textarea
                  id="lit-recommend-comment"
                  rows={3}
                  value={recommendComment}
                  onChange={(event) => setRecommendComment(event.target.value)}
                  placeholder="Почему вы рекомендуете эту книгу"
                />

                <footer className="lit-book-view__recommend-foot">
                  <button type="button" className="lit-book-view__btn is-ghost" onClick={closeRecommendModal}>
                    Отмена
                  </button>
                  <button
                    type="submit"
                    className="lit-book-view__btn is-primary"
                    disabled={recommendSending || !recommendRecipient?.userId}
                  >
                    {recommendSending ? "Отправка…" : "Отправить рекомендацию"}
                  </button>
                </footer>
              </form>
            </section>
          </div>

          {showBuyControls ? (
            <div
              className={`lit-book-view__buy-modal ${buyModalOpen ? "is-open" : ""}`}
              onClick={(event) => {
                if (event.target !== event.currentTarget) return;
                closeBuyModal();
              }}
            >
              <section className="lit-book-view__buy-inner" role="dialog" aria-modal="true" aria-label="Покупка книги">
                <header className="lit-book-view__buy-head">
                  <h3>Купить книгу</h3>
                  <button type="button" onClick={closeBuyModal}>
                    ✕
                  </button>
                </header>
                <div className="lit-book-view__buy-body">
                  <div className="lit-book-view__buy-book">
                    <div className="lit-book-view__buy-cover">
                      {book.coverUrl ? <img src={book.coverUrl} alt={book.title} loading="lazy" /> : null}
                    </div>
                    <div>
                      <strong>{book.title}</strong>
                      <span>
                        {book.author}
                        {book.publishYear ? ` · ${book.publishYear}` : ""}
                        {book.pagesCount ? ` · ${book.pagesCount} стр.` : ""}
                      </span>
                    </div>
                  </div>

                  <div className="lit-book-view__buy-options">
                    <button type="button" className="is-selected" disabled>
                      <div>
                        <strong>Покупка книги</strong>
                        <span>
                          {book.textVersionFormat ? `Формат ${book.textVersionFormat}` : "Электронная версия"}
                        </span>
                      </div>
                      <div className="lit-book-view__buy-price">
                        <b>{priceLabel}</b>
                        {hasDiscount ? (
                          <small>{`${oldPriceLabel} · скидка ${discountPercent}%`}</small>
                        ) : null}
                      </div>
                    </button>
                  </div>
                </div>
                <footer className="lit-book-view__buy-foot">
                  <div>
                    <span>Итого:</span>
                    <strong>{priceLabel}</strong>
                    {hasDiscount ? <small>{oldPriceLabel}</small> : null}
                  </div>
                  <button type="button" onClick={handleBuySubmit} disabled={buySubmitting || !canBuy}>
                    {buySubmitting ? "Создаем платеж…" : canBuy ? "Перейти к оплате →" : "Покупка недоступна"}
                  </button>
                </footer>
              </section>
            </div>
          ) : null}

          <ReactionParticlesLayer particles={reactionParticles} />
        </div>
      </div>
    </div>
  );
}
